import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import { useNavigate } from 'react-router-dom';
import { eventosAPI,
  mensajesAPI,
  acuerdosAPI,
  resenasAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { getPerfilDisplayName } from '../utils/miEntidadNombreDefecto';
import { resolveUploadsUrl } from '../utils/staticUrl';
import { resolveBandaFromUser } from '../utils/acuerdoParties';
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  Card,
  Heading,
  Badge,
  Icon,
  Tabs,
  Button,
  SimpleGrid,
  useDisclosure,
  NativeSelect,
  Flex,
  Input,
  Textarea,
  NumberInput,
  IconButton,
  Alert,
  Spinner,
  Center,
  Image,
  Tag,
  Wrap,
  WrapItem,
  Progress,
  Checkbox,
  Separator,
  Field,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { Avatar } from '@/components/ui/avatar';
import { TabList, TabPanels, Tab, TabPanel, Switch, tabsControlledValue, tabsOnValueChange } from '@/components/ui/chakra-v2-compat';
import { toaster } from '@/components/ui/toaster';
import { Tooltip } from '@/components/ui/tooltip';
import {
  FaCalendarCheck,
  FaMapMarkerAlt,
  FaClock,
  FaEuroSign,
  FaMusic,
  FaChurch,
  FaCheckCircle,
  FaHourglass,
  FaInfoCircle,
  FaEnvelope,
  FaPlus,
  FaSearch,
  FaHeart,
  FaTimes,
  FaStar,
  FaEye,
  FaComments,
  FaAward,
  FaHandshake,
  FaExclamationTriangle,
  FaTrash,
  FaYoutube,
  FaImage as FaImageIcon,
  FaLink,
} from 'react-icons/fa';
import AppLayout from './AppLayout.jsx';
import { useProductTour } from '../hooks/useProductTour';
import { bandaEventosSteps } from '../tours/bandaEventosTour';
import Search from './Search.jsx';
import ReviewForm from './ReviewForm.jsx';
import ActuacionMapPreview from './ActuacionMapPreview.jsx';
import { isEventoEfectivamenteFinalizado } from '../utils/eventoInicio.js';

/** Micro-interacción en acciones dentro de tarjetas de listado */
const eventCardActionButtonProps = {
  transition: 'transform 0.16s cubic-bezier(0.33, 1, 0.68, 1), box-shadow 0.16s ease',
  _hover: { transform: 'translateY(-2px)', boxShadow: 'md' },
  _active: { transform: 'scale(0.97)' },
};

/** Un solo aviso contextual en tarjetas del listado (sustituye varios Alert apilados). */
function getProximaEventCardContext(evento, userType, eventosWithActiveAcuerdos) {
  const hasAcuerdo = eventosWithActiveAcuerdos[evento.id]?.hasAcuerdo;
  if (evento.status === 'confirmed' || evento.status === 'completed') {
    return {
      status: 'success',
      text: 'Chat, acuerdo y documentación: todo en la ficha del evento.',
    };
  }
  if (userType === 'cofradia' && hasAcuerdo) {
    return {
      status: 'info',
      text: 'Hay un acuerdo en curso con una banda. Ábrelo desde la ficha del evento.',
    };
  }
  if (userType === 'banda' && evento.status === 'pending' && hasAcuerdo) {
    return {
      status: 'warning',
      text: 'Tienes una propuesta de acuerdo. Respóndela desde la ficha del evento.',
    };
  }
  return null;
}

const Events = ({ userType = 'banda' }) => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  useProductTour('banda-eventos', bandaEventosSteps, { autoStart: false, listenGlobal: userType === 'banda' });
  
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const eventListCardBg = useColorModeValue('gray.50', 'gray.700');
  const notesHighlightBg = useColorModeValue('blue.50', 'blue.900');
  const cancelledEventCardBg = useColorModeValue('red.50', 'gray.700');
  const shortlistCardBg = useColorModeValue('yellow.50', 'yellow.900');
  const eventCardBorderColor = useColorModeValue('blackAlpha.100', 'whiteAlpha.200');
  const eventCardHoverBorderColor = useColorModeValue('purple.200', 'purple.400');
  const eventCardMetaBg = useColorModeValue('white', 'gray.800');
  const eventCardFooterBg = useColorModeValue('gray.50', 'gray.900');
  const bannerOverlayGradient = useColorModeValue(
    'linear(to-t, blackAlpha.700, blackAlpha.400, transparent)',
    'linear(to-t, blackAlpha.800, blackAlpha.500, transparent)'
  );
  const { open: isOpen, onOpen, onClose } = useDisclosure();
  const { open: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const {
    open: isConfirmAcuerdoOpen,
    onOpen: onConfirmAcuerdoOpen,
    onClose: onConfirmAcuerdoCloseBase,
  } = useDisclosure();
  const { open: isInterestModalOpen, onOpen: onInterestModalOpen, onClose: onInterestModalClose } = useDisclosure();
  const { open: isDeleteEventoOpen, onOpen: onDeleteEventoOpen, onClose: onDeleteEventoClose } = useDisclosure();
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedBandaForAcuerdo, setSelectedBandaForAcuerdo] = useState(null);
  const [eventoToDelete, setEventoToDelete] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [isDeletingEvento, setIsDeletingEvento] = useState(false);
  const [acuerdoData, setAcuerdoData] = useState({
    descripcion: '',
    precio: 0,
    notas: '',
  });
  const [acuerdoProposalStep, setAcuerdoProposalStep] = useState(0);
  const [acuerdoAceptaDisclaimer, setAcuerdoAceptaDisclaimer] = useState(false);
  const [isSubmittingAcuerdoProposal, setIsSubmittingAcuerdoProposal] = useState(false);
  const [eventToShowInterest, setEventToShowInterest] = useState(null);
  const [sortBy, setSortBy] = useState('date-asc'); // date-asc, date-desc, status, name
  const [eventosListTab, setEventosListTab] = useState(0); // 0: Próximos, 1: Pasados, 2: Cancelados
  const [activeTabInModal, setActiveTabInModal] = useState(0); // 0: Detalles, 1: Buscar banda
  const routePrefix = userType === 'banda' ? '/banda' : '/cofradia';
  
  // Estado para reseñas y recomendaciones (solo para cofradías)
  // Para cofradías: IDs de eventos que ya tienen reseña/recomendación
  const [eventosConResena, setEventosConResena] = useState([]); // IDs de eventos que ya tienen reseña
  const [eventosConRecomendacion, setEventosConRecomendacion] = useState([]); // IDs de eventos que ya tienen aval
  
  // Estado para el modal de reseña
  const [eventoParaResenar, setEventoParaResenar] = useState(null);
  const { open: isReviewModalOpen, onOpen: onReviewModalOpen, onClose: onReviewModalClose } = useDisclosure();
  
  // Estado para eventos públicos (solo para bandas)
  const [eventosPublicos, setEventosPublicos] = useState([1, 2]); // IDs de eventos que son públicos
  const [todosPublicos, setTodosPublicos] = useState(false);
  const [createStep, setCreateStep] = useState(0);
  const [newEvent, setNewEvent] = useState({
    eventName: '',
    description: '',
    date: '',
    timeStart: '',
    timeEnd: '',
    locationStart: '',
    locationEnd: '',
    sameLocation: true,
    price: '',
    duration: '',
    notes: '',
  });

  const todayISO = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const minHoraHoy = useMemo(() => {
    const d = new Date();
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  }, []);

  const minutosDesdeHora = useCallback((hhmm) => {
    const s = String(hhmm || '').trim();
    const m = /^(\d{2}):(\d{2})$/.exec(s);
    if (!m) return null;
    const hh = Number(m[1]);
    const mm = Number(m[2]);
    if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
    return hh * 60 + mm;
  }, []);

  const isHoySeleccionado = newEvent.date === todayISO;

  useEffect(() => {
    if (userType !== 'cofradia') return;
    if (!isHoySeleccionado) return;
    const t = minutosDesdeHora(newEvent.timeStart);
    const min = minutosDesdeHora(minHoraHoy);
    if (t == null || min == null) return;
    if (t < min) {
      setNewEvent((p) => (p.timeStart === minHoraHoy ? p : { ...p, timeStart: minHoraHoy }));
    }
  }, [userType, isHoySeleccionado, newEvent.timeStart, minHoraHoy, minutosDesdeHora]);
  const [newEventCartel, setNewEventCartel] = useState(null);
  const [newEventFotos, setNewEventFotos] = useState([]);
  const [newEventVideos, setNewEventVideos] = useState([]);
  const [newEventVideoDraft, setNewEventVideoDraft] = useState({ titulo: '', url: '' });
  const [startCoordinates, setStartCoordinates] = useState(null); // { lat, lng }
  const [endCoordinates, setEndCoordinates] = useState(null); // { lat, lng }
  const [isGeocodingStart, setIsGeocodingStart] = useState(false);
  const [isGeocodingEnd, setIsGeocodingEnd] = useState(false);
  const [startGeocodingError, setStartGeocodingError] = useState(null);
  const [endGeocodingError, setEndGeocodingError] = useState(null);
  const [startSuggestions, setStartSuggestions] = useState([]);
  const [endSuggestions, setEndSuggestions] = useState([]);
  const [showStartSuggestions, setShowStartSuggestions] = useState(false);
  const [showEndSuggestions, setShowEndSuggestions] = useState(false);
  
  // Estado para eventos (inicialmente vacío, se carga desde la API)
  const [eventosBandaState, setEventosBandaState] = useState([]);
  const [isLoadingEventos, setIsLoadingEventos] = useState(true);
  
  // Estado para el shortlist de bandas para cada evento
  const [eventShortlists, setEventShortlists] = useState({});
  const [isAddingToShortlist, setIsAddingToShortlist] = useState(false);
  const [eventosWithActiveAcuerdos, setEventosWithActiveAcuerdos] = useState({}); // { eventoId: { hasAcuerdo: bool, acuerdo: object, conversacionId: string } }
  const [eventosWithConversations, setEventosWithConversations] = useState(new Set()); // Set de IDs de eventos que tienen conversaciones

  const computeDurationText = (start, end) => {
    const s = String(start || '').trim();
    const e = String(end || '').trim();
    if (!s || !e) return '';
    const [sh, sm] = s.split(':').map((x) => parseInt(x, 10));
    const [eh, em] = e.split(':').map((x) => parseInt(x, 10));
    if ([sh, sm, eh, em].some((n) => Number.isNaN(n))) return '';
    let mins = (eh * 60 + em) - (sh * 60 + sm);
    // Si la hora final es menor, asumimos que termina al día siguiente.
    if (mins < 0) mins += 24 * 60;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h <= 0 && m <= 0) return '';
    if (h > 0 && m > 0) return `${h}h ${m}min`;
    if (h > 0) return `${h}h`;
    return `${m}min`;
  };

  const CREATE_STEPS = [
    { title: 'Información básica', subtitle: 'Nombre y descripción del evento' },
    { title: 'Fecha y horas', subtitle: 'Indica cuándo empieza y cuándo termina (obligatorio)' },
    { title: 'Ubicaciones', subtitle: 'Salida y llegada con mapa' },
    { title: 'Detalles', subtitle: 'Notas adicionales' },
    { title: 'Multimedia', subtitle: 'Cartel, galería y vídeos' },
  ];

  const EVENT_ACUERDO_PROPOSE_STEPS = [
    { title: 'Contexto', subtitle: 'Banda seleccionada y datos del evento' },
    { title: 'Descripción del acuerdo', subtitle: 'Explica con claridad la propuesta (obligatorio)' },
    { title: 'Precio y notas', subtitle: 'Importe y aclaraciones opcionales' },
    { title: 'Resumen y envío', subtitle: 'Confirma antes de enviar la solicitud' },
  ];

  const canAdvanceCreateStep = (stepIdx) => {
    if (stepIdx === 0) return String(newEvent.eventName || '').trim().length > 0;
    if (stepIdx === 1) {
      if (!newEvent.date) return false;
      if (!String(newEvent.timeStart || '').trim()) return false;
      if (!String(newEvent.timeEnd || '').trim()) return false;
      return true;
    }
    if (stepIdx === 2) {
      if (!String(newEvent.locationStart || '').trim()) return false;
      if (!newEvent.sameLocation && !String(newEvent.locationEnd || '').trim()) return false;
      return true;
    }
    return true;
  };

  const closeCreateModal = () => {
    setCreateStep(0);
    onCreateClose();
  };

  const onConfirmAcuerdoClose = () => {
    setAcuerdoProposalStep(0);
    onConfirmAcuerdoCloseBase();
  };

  // Función para cargar eventos desde la API
  const loadEventos = async () => {
    setIsLoadingEventos(true);
    try {
      const response = await eventosAPI.getAll();
      const eventos = response.data?.data || response.data || [];
      
      // Construir shortlists desde los datos del API
      const shortlistsFromAPI = {};
      
      // Transformar datos de la API al formato del componente
      const eventosTransformados = eventos.map(evento => {
        // Extraer shortlist de cada evento
        if (evento.shortlist && evento.shortlist.length > 0) {
          shortlistsFromAPI[evento.id] = evento.shortlist.map(item => ({
            id: item.banda.id,
            name: item.banda.nombre,
            avatar: item.banda.avatar,
            type: item.banda.tipoGrupo || 'banda',
            userId: item.banda.userId,
            location: '',
            distance: 0,
            trustedBy: 0,
          }));
        }
        
        return {
          id: evento.id,
          eventName: evento.nombre,
          date: evento.fecha ? evento.fecha.split('T')[0] : '',
          timeStart: evento.horaInicio || evento.hora || '',
          timeEnd: evento.horaFin || '',
          locationStart: evento.ubicacionSalida || evento.ubicacion || '',
          locationEnd: evento.ubicacionEntrada || '',
          sameLocation: !!(evento.ubicacionEntrada && (evento.ubicacionEntrada === (evento.ubicacionSalida || evento.ubicacion))),
          latStart: evento.latSalida ?? evento.lat ?? null,
          lngStart: evento.lngSalida ?? evento.lng ?? null,
          latEnd: evento.latEntrada ?? null,
          lngEnd: evento.lngEntrada ?? null,
          status: evento.status?.toLowerCase() || 'pending',
          price: evento.precio || 0,
          duration: evento.duracion || '',
          notes: evento.notas || '',
          cancelReason: evento.cancelReason || '',
          posterUrl: evento.cartel ? resolveUploadsUrl(evento.cartel) : '',
          photosCount: Array.isArray(evento.fotos) ? evento.fotos.length : 0,
          videosCount: Array.isArray(evento.videos) ? evento.videos.length : 0,
          // Para cofradías: datos de la banda asignada
          bandaId: evento.bandaId,
          bandaName: evento.banda?.nombre,
          bandaAvatar: evento.banda?.avatar,
          bandaUserId: evento.banda?.userId,
          // Para bandas: datos de la cofradía que creó el evento
          cofradiaId: evento.cofradiaId,
          cofradiaName: evento.cofradia?.nombre,
          cofradiaAvatar: evento.cofradia?.avatar,
          cofradiaUserId: evento.cofradia?.userId,
          // Contadores
          shortlistCount: evento._count?.shortlist || 0,
          interesadosCount: evento._count?.interesados || 0,
        };
      });
      
      // Cargar estado de acuerdos primero
      await loadActiveAcuerdosForEvents(eventosTransformados);
      
      // Cargar información de qué eventos tienen conversaciones
      await loadEventosWithConversations();
      
      // Para bandas: filtrar eventos para mostrar solo aquellos con acuerdo activo, confirmados o cancelados
      let eventosFiltrados = eventosTransformados;
      if (userType === 'banda') {
        // Obtener IDs de eventos con acuerdos activos
        const eventosConAcuerdo = await getEventosWithAcuerdos(eventosTransformados);
        
        eventosFiltrados = eventosTransformados.filter(evento => {
          // Mostrar si el evento está confirmado, completado o cancelado
          if (evento.status === 'confirmed' || evento.status === 'completed' || evento.status === 'cancelled') {
            return true;
          }
          // Mostrar si hay un acuerdo activo (PENDING o ACCEPTED)
          if (eventosConAcuerdo[evento.id]) {
            return true;
          }
          return false;
        });
      }
      
      setEventosBandaState(eventosFiltrados);
      setEventShortlists(shortlistsFromAPI);
    } catch (error) {
      console.error('Error cargando eventos:', error);
      setEventosBandaState([]);
    } finally {
      setIsLoadingEventos(false);
    }
  };

  // Cargar eventos cuando el componente se monta o cambia el userType
  useEffect(() => {
    loadEventos();
  }, [userType]);

  // Buscar sugerencias de ubicación (SALIDA)
  useEffect(() => {
    const searchLocations = async () => {
      if (!newEvent.locationStart || newEvent.locationStart.trim().length < 3) {
        setStartSuggestions([]);
        setShowStartSuggestions(false);
        setStartCoordinates(null);
        setStartGeocodingError(null);
        return;
      }

      setIsGeocodingStart(true);
      setStartGeocodingError(null);

      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
            newEvent.locationStart,
          )}&limit=5&countrycodes=es`,
          {
            headers: {
              'User-Agent': 'Cadencya/1.0',
            },
          },
        );

        const data = await response.json();

        if (data && data.length > 0) {
          const suggestions = data.map((item) => ({
            displayName: item.display_name,
            lat: parseFloat(item.lat),
            lng: parseFloat(item.lon),
          }));
          setStartSuggestions(suggestions);
          setShowStartSuggestions(true);

          // Auto-seleccionar la primera si solo hay una
          if (suggestions.length === 1) {
            setStartCoordinates({
              lat: suggestions[0].lat,
              lng: suggestions[0].lng,
            });
            if (newEvent.sameLocation) setEndCoordinates({ lat: suggestions[0].lat, lng: suggestions[0].lng });
          }
        } else {
          setStartSuggestions([]);
          setShowStartSuggestions(false);
          setStartGeocodingError('Ubicación no encontrada');
          setStartCoordinates(null);
        }
      } catch (err) {
        console.error('Error buscando ubicaciones:', err);
        setStartGeocodingError('Error al buscar ubicación');
        setStartSuggestions([]);
        setShowStartSuggestions(false);
        setStartCoordinates(null);
      } finally {
        setIsGeocodingStart(false);
      }
    };

    // Debounce para evitar muchas peticiones
    const timeoutId = setTimeout(searchLocations, 500);
    return () => clearTimeout(timeoutId);
  }, [newEvent.locationStart, newEvent.sameLocation]);

  // Buscar sugerencias de ubicación (ENTRADA)
  useEffect(() => {
    const searchLocations = async () => {
      if (newEvent.sameLocation) {
        setEndSuggestions([]);
        setShowEndSuggestions(false);
        setEndGeocodingError(null);
        setEndCoordinates(startCoordinates);
        return;
      }

      if (!newEvent.locationEnd || newEvent.locationEnd.trim().length < 3) {
        setEndSuggestions([]);
        setShowEndSuggestions(false);
        setEndCoordinates(null);
        setEndGeocodingError(null);
        return;
      }

      setIsGeocodingEnd(true);
      setEndGeocodingError(null);

      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
            newEvent.locationEnd,
          )}&limit=5&countrycodes=es`,
          {
            headers: {
              'User-Agent': 'Cadencya/1.0',
            },
          },
        );

        const data = await response.json();

        if (data && data.length > 0) {
          const suggestions = data.map((item) => ({
            displayName: item.display_name,
            lat: parseFloat(item.lat),
            lng: parseFloat(item.lon),
          }));
          setEndSuggestions(suggestions);
          setShowEndSuggestions(true);

          if (suggestions.length === 1) {
            setEndCoordinates({ lat: suggestions[0].lat, lng: suggestions[0].lng });
          }
        } else {
          setEndSuggestions([]);
          setShowEndSuggestions(false);
          setEndGeocodingError('Ubicación no encontrada');
          setEndCoordinates(null);
        }
      } catch (err) {
        console.error('Error buscando ubicaciones:', err);
        setEndGeocodingError('Error al buscar ubicación');
        setEndSuggestions([]);
        setShowEndSuggestions(false);
        setEndCoordinates(null);
      } finally {
        setIsGeocodingEnd(false);
      }
    };

    const timeoutId = setTimeout(searchLocations, 500);
    return () => clearTimeout(timeoutId);
  }, [newEvent.locationEnd, newEvent.sameLocation, startCoordinates]);

  // Duración: si hay hora final, se calcula automáticamente y queda read-only.
  useEffect(() => {
    const computed = computeDurationText(newEvent.timeStart, newEvent.timeEnd);
    setNewEvent((p) => (p.duration === computed ? p : { ...p, duration: computed }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newEvent.timeStart, newEvent.timeEnd]);

  // Obtener eventos con acuerdos activos (retorna objeto)
  const getEventosWithAcuerdos = async (eventos) => {
    try {
      const conversacionesResponse = await mensajesAPI.getConversaciones();
      const conversaciones = conversacionesResponse.data;
      
      const eventosWithAcuerdos = {};
      
      for (const evento of eventos) {
        const conversacionesEvento = conversaciones.filter(c => c.evento?.id === evento.id);
        
        let hasActiveAcuerdo = false;
        
        for (const conv of conversacionesEvento) {
          try {
            const acuerdoResponse = await acuerdosAPI.getActive(conv.id);
            const acuerdo = acuerdoResponse.data;
            if (acuerdo && (acuerdo.status === 'PENDING' || acuerdo.status === 'ACCEPTED')) {
              hasActiveAcuerdo = true;
              break;
            }
          } catch (error) {
            // No hay acuerdo activo en esta conversación
          }
        }
        
        eventosWithAcuerdos[evento.id] = hasActiveAcuerdo;
      }
      
      return eventosWithAcuerdos;
    } catch (error) {
      console.error('Error obteniendo acuerdos:', error);
      return {};
    }
  };

  // Cargar qué eventos tienen conversaciones
  const loadEventosWithConversations = async () => {
    try {
      const conversacionesResponse = await mensajesAPI.getConversaciones();
      const conversaciones = conversacionesResponse.data;
      
      // Crear un Set con los IDs de eventos que tienen conversaciones
      const eventosConConversaciones = new Set(
        conversaciones
          .filter(c => c.evento?.id)
          .map(c => c.evento.id)
      );
      
      setEventosWithConversations(eventosConConversaciones);
    } catch (error) {
      console.error('Error cargando conversaciones de eventos:', error);
    }
  };

  // Cargar acuerdos activos para todos los eventos
  const loadActiveAcuerdosForEvents = async (eventos) => {
    try {
      // Obtener todas las conversaciones
      const conversacionesResponse = await mensajesAPI.getConversaciones();
      const conversaciones = conversacionesResponse.data;
      
      const eventosWithAcuerdos = {};
      
      // Para cada evento, verificar si hay conversaciones con acuerdos activos
      for (const evento of eventos) {
        // Encontrar conversaciones asociadas a este evento
        const conversacionesEvento = conversaciones.filter(c => c.evento?.id === evento.id);
        
        let activeAcuerdo = null;
        let conversacionId = null;
        
        for (const conv of conversacionesEvento) {
          try {
            const acuerdoResponse = await acuerdosAPI.getActive(conv.id);
            const acuerdo = acuerdoResponse.data;
            if (acuerdo && (acuerdo.status === 'PENDING' || acuerdo.status === 'ACCEPTED')) {
              activeAcuerdo = acuerdo;
              conversacionId = conv.id;
              break;
            }
          } catch (error) {
            // No hay acuerdo activo en esta conversación
          }
        }
        
        eventosWithAcuerdos[evento.id] = {
          hasAcuerdo: !!activeAcuerdo,
          acuerdo: activeAcuerdo,
          conversacionId: conversacionId,
        };
      }
      
      setEventosWithActiveAcuerdos(eventosWithAcuerdos);
    } catch (error) {
      console.error('Error cargando acuerdos activos:', error);
    }
  };

  // IDs de eventos en los que el usuario (banda o cofradía) ya dejó reseña (para ocultar «Dejar reseña»)
  useEffect(() => {
    const loadResenasEscritasEventIds = async () => {
      try {
        const resenasResponse = await resenasAPI.getEscritas();
        const resenas = resenasResponse.data || [];
        const eventosConResenaIds = resenas.map((r) => r.evento?.id).filter(Boolean);
        setEventosConResena(eventosConResenaIds);
      } catch (error) {
        console.error('Error cargando reseñas escritas:', error);
      }
    };

    loadResenasEscritasEventIds();
  }, [userType]);

  const handleViewDetails = (evento) => {
    // Navegar a la página de detalles del evento
    navigate(`${routePrefix}/eventos/${evento.id}`, { state: { evento } });
  };

  const handleSelectLocationSuggestion = (suggestion) => {
    setNewEvent({ ...newEvent, locationStart: suggestion.displayName });
    setStartCoordinates({ lat: suggestion.lat, lng: suggestion.lng });
    setShowStartSuggestions(false);
    setStartSuggestions([]);
    if (newEvent.sameLocation) {
      setNewEvent((p) => ({ ...p, locationEnd: suggestion.displayName }));
      setEndCoordinates({ lat: suggestion.lat, lng: suggestion.lng });
    }
  };

  const handleCreateEvent = async () => {
    try {
      if (userType === 'cofradia' && newEvent.date === todayISO) {
        const nowMin = minutosDesdeHora(minHoraHoy);
        const iniMin = minutosDesdeHora(newEvent.timeStart);
        if (nowMin != null && iniMin != null && iniMin < nowMin) {
          toaster.create({
            title: 'Hora no válida',
            description: 'Para la fecha de hoy, la hora de inicio no puede ser anterior a la hora actual.',
            type: 'warning',
            duration: 6000,
            closable: true,
          });
          return;
        }
      }
      if (!String(newEvent.timeEnd || '').trim()) {
        toaster.create({
          title: 'Hora final obligatoria',
          description: 'Indica la hora final del evento para poder crear el evento.',
          type: 'warning',
          duration: 6000,
          closable: true,
        });
        return;
      }

      // Preparar datos para la API
      const eventoData = {
        nombre: newEvent.eventName,
        descripcion: String(newEvent.description || '').trim() || undefined,
        fecha: newEvent.date,
        horaInicio: newEvent.timeStart,
        horaFin: newEvent.timeEnd || null,
        ubicacionSalida: newEvent.locationStart,
        ubicacionEntrada: newEvent.sameLocation ? newEvent.locationStart : (newEvent.locationEnd || null),
        latSalida: startCoordinates?.lat || null,
        lngSalida: startCoordinates?.lng || null,
        latEntrada: newEvent.sameLocation ? (startCoordinates?.lat || null) : (endCoordinates?.lat || null),
        lngEntrada: newEvent.sameLocation ? (startCoordinates?.lng || null) : (endCoordinates?.lng || null),
        // precio eliminado de la UI (se mantiene en backend para compatibilidad)
        duracion: newEvent.timeEnd ? computeDurationText(newEvent.timeStart, newEvent.timeEnd) : newEvent.duration,
        notas: newEvent.notes,
        videos: newEventVideos.length > 0 ? newEventVideos : undefined,
      };
      
      const response = await eventosAPI.create(eventoData);
      
      if (response.data) {
        const createdId = response.data.id;
        // Subida de media (best-effort): no bloquea la creación del evento
        try {
          if (newEventCartel) {
            await eventosAPI.uploadCartel(createdId, newEventCartel);
          }
          if (newEventFotos && newEventFotos.length > 0) {
            await eventosAPI.uploadFotos(createdId, newEventFotos);
          }
        } catch (err) {
          console.warn('Error subiendo media de evento:', err);
          toaster.create({
            title: 'Evento creado, pero hubo un problema con la multimedia',
            description: 'El cartel/galería no se pudo subir. Puedes editarlo más tarde.',
            type: 'warning',
            duration: 7000,
            closable: true,
          });
        }

        // Añadir el nuevo evento al estado local
        const nuevoEvento = {
          id: response.data.id,
          eventName: response.data.nombre,
          date: response.data.fecha ? response.data.fecha.split('T')[0] : '',
          timeStart: response.data.horaInicio || response.data.hora || '',
          timeEnd: response.data.horaFin || '',
          locationStart: response.data.ubicacionSalida || response.data.ubicacion || '',
          locationEnd: response.data.ubicacionEntrada || '',
          sameLocation: !!(response.data.ubicacionEntrada && (response.data.ubicacionEntrada === (response.data.ubicacionSalida || response.data.ubicacion))),
          latStart: response.data.latSalida ?? response.data.lat ?? null,
          lngStart: response.data.lngSalida ?? response.data.lng ?? null,
          latEnd: response.data.latEntrada ?? null,
          lngEnd: response.data.lngEntrada ?? null,
          status: response.data.status?.toLowerCase() || 'pending',
          price: response.data.precio,
          duration: response.data.duracion,
          notes: response.data.notas,
          bandaId: response.data.bandaId,
          bandaName: response.data.banda?.nombre,
          bandaAvatar: response.data.banda?.avatar,
          cofradiaId: response.data.cofradiaId,
          cofradiaName: response.data.cofradia?.nombre,
          cofradiaAvatar: response.data.cofradia?.avatar,
        };
        
        setEventosBandaState(prev => [nuevoEvento, ...prev]);
        
        // Resetear formulario
        setNewEvent({
          eventName: '',
          description: '',
          date: '',
          timeStart: '',
          timeEnd: '',
          locationStart: '',
          locationEnd: '',
          sameLocation: true,
          price: '',
          duration: '',
          notes: '',
        });
        setNewEventCartel(null);
        setNewEventFotos([]);
        setNewEventVideos([]);
        setNewEventVideoDraft({ titulo: '', url: '' });
        setStartCoordinates(null);
        setEndCoordinates(null);
        setStartGeocodingError(null);
        setEndGeocodingError(null);
        setStartSuggestions([]);
        setEndSuggestions([]);
        setShowStartSuggestions(false);
        setShowEndSuggestions(false);
        
        setCreateStep(0);
        onCreateClose();
        
        // Mostrar mensaje de éxito
        toaster.create({
          title: '¡Evento creado!',
          description: 'Ahora puedes buscar una banda para este evento.',
          type: 'success',
          duration: 5000,
          closable: true,
        });
      }
    } catch (error) {
      console.error('Error al crear evento:', error);
      toaster.create({
        title: 'Error al crear evento',
        description: error.response?.data?.message || 'Error al crear el evento. Por favor, inténtalo de nuevo.',
        type: 'error',
        duration: 5000,
        closable: true,
      });
    }
  };

  const handleSearchBanda = (evento) => {
    // Navegar a la página de detalles con el tab de búsqueda
    navigate(`${routePrefix}/eventos/${evento.id}?tab=buscar`, { state: { evento } });
  };

  const handleOpenDeleteModal = (evento) => {
    setEventoToDelete(evento);
    setCancelReason('');
    onDeleteEventoOpen();
  };

  const handleDeleteEvento = async () => {
    if (!cancelReason.trim()) {
      toaster.create({
        title: 'Motivo requerido',
        description: 'Por favor, indica el motivo de la cancelación',
        type: 'warning',
        duration: 3000,
        closable: true,
      });
      return;
    }

    setIsDeletingEvento(true);
    try {
      await eventosAPI.delete(eventoToDelete.id, cancelReason);
      
      toaster.create({
        title: 'Evento cancelado',
        description: 'El evento ha sido cancelado y se han notificado a todos los participantes.',
        type: 'success',
        duration: 5000,
        closable: true,
      });

      onDeleteEventoClose();
      setEventoToDelete(null);
      setCancelReason('');
      
      // Recargar eventos
      await loadEventos();
    } catch (error) {
      console.error('Error eliminando evento:', error);
      toaster.create({
        title: 'Error',
        description: error.response?.data?.message || 'Error al cancelar el evento',
        type: 'error',
        duration: 5000,
        closable: true,
      });
    } finally {
      setIsDeletingEvento(false);
    }
  };

  const handleAddToShortlist = async (banda, mensaje = '') => {
    if (!selectedEvent) return;
    
    const eventId = selectedEvent.id;
    const currentShortlist = eventShortlists[eventId] || [];
    
    // Evitar duplicados
    if (currentShortlist.some(b => b.id === banda.id)) {
      return;
    }
    
    setIsAddingToShortlist(true);
    try {
      // 1. Crear o buscar conversación existente
      let conversacionId;
      try {
        const conversacionResponse = await mensajesAPI.createConversacion(
          banda.userId,
          eventId
        );
        conversacionId = conversacionResponse.data.id;
      } catch (error) {
        // Si ya existe la conversación, obtener su ID del error o buscarla
        if (error.response?.data?.conversacionId) {
          conversacionId = error.response.data.conversacionId;
        } else {
          throw error;
        }
      }

      // 2. Si hay mensaje, enviarlo
      if (mensaje && conversacionId) {
        await mensajesAPI.sendMensaje(conversacionId, mensaje);
      }

      // 3. Llamar al API para añadir al shortlist
      const response = await eventosAPI.addToShortlist(eventId, banda.id, {
        openedChatWithMessage: Boolean(mensaje && String(mensaje).trim()),
      });
      
      // 4. Actualizar estado local con los datos completos del backend (incluyendo userId)
      setEventShortlists(prev => ({
        ...prev,
        [eventId]: [...(prev[eventId] || []), {
          id: response.data.banda.id,
          name: response.data.banda.nombre,
          avatar: response.data.banda.avatar,
          type: response.data.banda.tipoGrupo || 'banda',
          userId: response.data.banda.userId,
          location: banda.location || '',
          distance: banda.distance || 0,
          trustedBy: banda.trustedBy || 0,
          conversacionId: conversacionId, // Guardar el ID de la conversación
        }]
      }));
      
      toaster.create({
        title: mensaje ? '¡Mensaje enviado!' : 'Añadida a favoritas',
        description: mensaje 
          ? 'Se ha enviado tu mensaje y la banda ha sido añadida a favoritas.'
          : 'La banda ha sido añadida a favoritas. Podrás escribirle cuando quieras desde Mensajes.',
        type: 'success',
        duration: 5000,
        closable: true,
      });
      
      console.log(`Banda ${banda.name || banda.nombre} añadida al shortlist del evento ${eventId}`);
    } catch (error) {
      console.error('Error añadiendo al shortlist:', error);
      toaster.create({
        title: 'Error',
        description: error.response?.data?.message || 'Error al contactar con la banda',
        type: 'error',
        duration: 5000,
        closable: true,
      });
    } finally {
      setIsAddingToShortlist(false);
    }
  };

  const handleRemoveFromShortlist = async (eventId, bandaId) => {
    try {
      // Llamar al API para eliminar del shortlist
      await eventosAPI.removeFromShortlist(eventId, bandaId);
      
      // Actualizar estado local
      setEventShortlists(prev => {
        const currentShortlist = prev[eventId] || [];
        return {
          ...prev,
          [eventId]: currentShortlist.filter(b => b.id !== bandaId)
        };
      });
      
      console.log(`Banda ${bandaId} eliminada del shortlist del evento ${eventId}`);
    } catch (error) {
      console.error('Error eliminando del shortlist:', error);
      toaster.create({
        title: 'Error',
        description: error.response?.data?.message || 'Error al eliminar la banda del shortlist',
        type: 'error',
        duration: 5000,
        closable: true,
      });
    }
  };

  const handleSelectBandaForAcuerdo = (banda, evento = null) => {
    const targetEvento = evento || eventForSearch;
    if (!targetEvento) return;

    setSelectedBandaForAcuerdo({ banda, evento: targetEvento });
    setAcuerdoProposalStep(0);
    setAcuerdoAceptaDisclaimer(false);
    // Inicializar datos del acuerdo con información del evento
    setAcuerdoData({
      descripcion: `Propuesta de acuerdo para el evento "${targetEvento.eventName}"`,
      precio: targetEvento.price || 0,
      notas: targetEvento.notes || '',
    });
    onConfirmAcuerdoOpen();
  };

  const canAdvanceAcuerdoProposalStep = (stepIdx) => {
    if (stepIdx === 0) return true;
    if (stepIdx === 1) return String(acuerdoData.descripcion || '').trim().length > 0;
    if (stepIdx === 2) return true;
    if (stepIdx === 3) return String(acuerdoData.descripcion || '').trim().length > 0 && acuerdoAceptaDisclaimer;
    return true;
  };

  const handleConfirmAcuerdo = async () => {
    if (!selectedBandaForAcuerdo) return;
    if (!acuerdoAceptaDisclaimer) {
      toaster.create({
        title: 'Aviso legal obligatorio',
        description: 'Debes aceptar el aviso legal para proponer el acuerdo.',
        type: 'warning',
        duration: 5000,
        closable: true,
      });
      return;
    }

    const { banda, evento } = selectedBandaForAcuerdo;

    setIsSubmittingAcuerdoProposal(true);
    try {
      // 1. Crear o obtener conversación con la banda
      let conversacion;
      try {
        conversacion = await mensajesAPI.createConversacion(banda.userId, evento.id);
      } catch (error) {
        // Si la conversación ya existe, obtenerla de las conversaciones existentes
        if (error.response?.data?.message?.includes('ya existe')) {
          const conversaciones = await mensajesAPI.getConversaciones();
          conversacion = conversaciones.data.find(c => 
            c.evento?.id === evento.id && 
            c.participantes.some(p => p.user?.banda?.id === banda.id)
          );
          if (!conversacion) throw error;
        } else {
          throw error;
        }
      }
      
      // 2. Crear solicitud de acuerdo con los datos del formulario
      await acuerdosAPI.create({
        conversacionId: conversacion.data?.id || conversacion.id,
        descripcion: acuerdoData.descripcion,
        fecha: evento.date,
        precio: acuerdoData.precio,
        notas: acuerdoData.notas,
        aceptaDisclaimer: true,
      });
      
      // 3. Enviar mensaje al chat notificando la propuesta
      const conversacionId = conversacion.data?.id || conversacion.id;
      let mensajePropuesta = '🤝 He propuesto un acuerdo para este evento.';
      if (acuerdoData.precio) {
        mensajePropuesta += `\n\n💰 Precio propuesto: ${acuerdoData.precio}€`;
      }
      if (acuerdoData.descripcion) {
        mensajePropuesta += `\n\n📋 Descripción: ${acuerdoData.descripcion}`;
      }
      if (acuerdoData.notas) {
        mensajePropuesta += `\n\n📝 Notas: ${acuerdoData.notas}`;
      }
      
      try {
        await mensajesAPI.sendMensaje(conversacionId, mensajePropuesta);
        console.log('Mensaje de propuesta enviado al chat');
      } catch (error) {
        console.error('Error enviando mensaje de propuesta:', error);
      }
      
      console.log('Acuerdo propuesto a la banda:', banda.name, 'para evento:', evento.eventName);
      
      // El evento permanece en estado PENDING hasta que la banda acepte
      // NO actualizar el estado del evento a 'confirmed'
      
      // Cerrar modales y volver a tab de detalles
      onConfirmAcuerdoClose();
      setActiveTabInModal(0);
      
      // Limpiar selección
      setSelectedBandaForAcuerdo(null);
      
      // Cerrar modal de detalles
      onClose();
      
      toaster.create({
        title: '¡Propuesta enviada!',
        description: `Se ha enviado la propuesta de acuerdo a ${banda.name}. El evento permanecerá pendiente hasta que la banda acepte.`,
        type: 'success',
        duration: 6000,
        closable: true,
      });
      
      // Actualizar el estado de acuerdos activos
      setEventosWithActiveAcuerdos(prev => ({
        ...prev,
        [evento.id]: true
      }));
      
      // Navegar a mensajes con el ID de la conversación para seleccionarla automáticamente
      navigate(`${routePrefix}/mensajes?conversacion=${conversacionId}`);
    } catch (error) {
      console.error('Error proponiendo acuerdo:', error);
      toaster.create({
        title: 'Error',
        description: error.response?.data?.message || 'Error al proponer el acuerdo',
        type: 'error',
        duration: 5000,
        closable: true,
      });
    } finally {
      setIsSubmittingAcuerdoProposal(false);
    }
  };

  const handleNavigateToEventChat = async (evento) => {
    try {
      // Determinar la pestaña según el status del evento
      let tab = ''; // Por defecto no especifica pestaña (se quedará en la actual)
      if (evento.status === 'cancelled') {
        tab = 'tab=2'; // Pestaña "Cancelados" (índice 2)
      } else if (evento.status === 'completed') {
        tab = 'tab=1'; // Pestaña "Finalizados" (índice 1)
      } else {
        tab = 'tab=0'; // Pestaña "Activos" (índice 0)
      }
      
      // Navegar a mensajes con la pestaña y el evento
      navigate(`${routePrefix}/mensajes?${tab}&evento=${evento.id}`);
    } catch (error) {
      console.error('Error al navegar al chat:', error);
      // En caso de error, navegar a mensajes sin selección
      navigate(`${routePrefix}/mensajes`);
    }
  };

  const handleReviewFromEvents = (evento) => {
    // Prepare event data for the review form
    // Both bands and cofradias can leave reviews (bidirectional system)
    const eventoForReview = {
      id: evento.id,
      nombre: evento.eventName,
      fecha: evento.date,
    };
    
    // Add the other party's information depending on who is logged in
    if (userType === 'banda') {
      // Banda reviewing cofradia
      eventoForReview.cofradiaId = evento.cofradiaId;
      eventoForReview.cofradia = {
        id: evento.cofradiaId,
        nombre: evento.cofradiaName,
        avatar: evento.cofradiaAvatar,
      };
    } else {
      // Cofradia reviewing banda
      eventoForReview.bandaId = evento.bandaId;
      eventoForReview.banda = {
        id: evento.bandaId,
        nombre: evento.bandaName,
        avatar: evento.bandaAvatar,
      };
    }
    
    setEventoParaResenar(eventoForReview);
    onReviewModalOpen();
  };
  
  const handleReviewSubmitted = async () => {
    // Reload the list of events with reviews
    try {
      const resenasResponse = await resenasAPI.getEscritas();
      const resenas = resenasResponse.data || [];
      const eventosConResenaIds = resenas.map(r => r.evento?.id).filter(Boolean);
      setEventosConResena(eventosConResenaIds);
    } catch (error) {
      console.error('Error recargando reseñas:', error);
    }
  };

  const handleRecommendFromEvents = (evento) => {
    // Redirigir a mensajes con la otra parte (banda o cofradía)
    // TODO: En una implementación real, buscaríamos la conversación correspondiente
    const otherPartyName = userType === 'banda' ? evento.cofradiaName : evento.bandaName;
    navigate(`${routePrefix}/mensajes`, { 
      state: { 
        openRecommend: true, 
        targetName: otherPartyName 
      } 
    });
  };

  // Manejar cuando una banda muestra interés en un evento
  const handleShowInterest = (evento) => {
    setEventToShowInterest(evento);
    onInterestModalOpen();
  };

  const [isShowingInterest, setIsShowingInterest] = useState(false);
  const [interestMessage, setInterestMessage] = useState('');

  const confirmShowInterest = async () => {
    if (!eventToShowInterest) return;
    
    setIsShowingInterest(true);
    try {
      // Llamar al API para mostrar interés y crear conversación
      const response = await eventosAPI.showInterest(eventToShowInterest.id, {
        mensaje: interestMessage,
      });
      
      console.log('Interés confirmado:', response.data);
      
      // Actualizar el estado del evento de pending a interested (interesado/conectado)
      setEventosBandaState(prevEvents => 
        prevEvents.map(event => 
          event.id === eventToShowInterest.id 
            ? { ...event, status: 'interested', conversacionId: response.data.conversacionId }
            : event
        )
      );
      
      onInterestModalClose();
      setEventToShowInterest(null);
      setInterestMessage('');

      // Mostrar toast de éxito con opción de ir a mensajes
      toaster.create({
        title: '¡Interés registrado!',
        description: response.data.conversacionId 
          ? 'Se ha creado una conversación con la cofradía. Puedes ir a Mensajes para contactar con ellos.'
          : 'Tu interés ha sido registrado correctamente.',
        type: 'success',
        duration: 7000,
        closable: true,
      });
    } catch (error) {
      console.error('Error mostrando interés:', error);
      toaster.create({
        title: 'Error',
        description: error.response?.data?.message || 'Error al mostrar interés en el evento',
        type: 'error',
        duration: 5000,
        closable: true,
      });
    } finally {
      setIsShowingInterest(false);
    }
  };

  // Manejar cuando una banda no está interesada en un evento
  const handleRejectEvent = async (evento) => {
    console.log('Banda rechaza evento:', evento.eventName);
    
    // Marcar el evento como rechazado para animación de salida
    setEventosBandaState(prevEvents => 
      prevEvents.map(event => 
        event.id === evento.id 
          ? { ...event, status: 'rejecting' }
          : event
      )
    );
    
    try {
      // Llamar al API para rechazar el evento
      await eventosAPI.rejectEvent(evento.id);
    } catch (error) {
      console.error('Error rechazando evento:', error);
    }
    
    // Después de la animación, eliminar el evento del estado
    setTimeout(() => {
      setEventosBandaState(prevEvents => 
        prevEvents.filter(event => event.id !== evento.id)
      );
    }, 500); // Duración de la animación
  };

  const getShortlistedIds = (eventId) => {
    const shortlist = eventShortlists[eventId] || [];
    return shortlist.map(b => b.id);
  };

  const sortEvents = (events) => {
    const sorted = [...events];
    
    switch (sortBy) {
      case 'date-asc':
        return sorted.sort((a, b) => new Date(a.date) - new Date(b.date));
      case 'date-desc':
        return sorted.sort((a, b) => new Date(b.date) - new Date(a.date));
      case 'status':
        const statusOrder = { pending: 1, confirmed: 2, completed: 3, cancelled: 4 };
        return sorted.sort((a, b) => statusOrder[a.status] - statusOrder[b.status]);
      case 'name':
        return sorted.sort((a, b) => a.eventName.localeCompare(b.eventName));
      default:
        return sorted;
    }
  };

  // Usar los eventos cargados desde la API
  const eventos = eventosBandaState;
  const otherPartyKey = userType === 'banda' ? 'cofradiaName' : 'bandaName';

  // Función para navegar al perfil de la entidad
  const handleGoToProfile = (evento) => {
    if (!evento[otherPartyKey]) return; // No hay entidad asignada
    
    const entityType = userType === 'banda' ? 'cofradia' : 'banda';
    const entityIdKey = userType === 'banda' ? 'cofradiaId' : 'bandaId';
    const entityId = evento[entityIdKey];
    
    if (!entityId) {
      console.warn('No se encontró el ID de la entidad');
      return;
    }
    
    const profilePath = `/ver-perfil-${entityType}?id=${entityId}`;
    navigate(profilePath);
  };

  // Funciones para manejar eventos públicos
  const handleToggleEventoPublico = (eventoId) => {
    setEventosPublicos(prev => {
      if (prev.includes(eventoId)) {
        return prev.filter(id => id !== eventoId);
      } else {
        return [...prev, eventoId];
      }
    });
  };

  const handleToggleTodosPublicos = () => {
    const nuevoEstado = !todosPublicos;
    setTodosPublicos(nuevoEstado);
    
    if (nuevoEstado) {
      // Hacer públicos todos los eventos confirmados o completados
      const proximosIds = eventos
        .filter(e => e.status === 'confirmed' || e.status === 'completed')
        .map(e => e.id);
      setEventosPublicos(proximosIds);
    } else {
      // Quitar todos los eventos públicos
      setEventosPublicos([]);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'green';
      case 'pending': return 'orange';
      case 'interested': return 'purple';
      case 'completed': return 'blue';
      case 'cancelled': return 'red';
      default: return 'gray';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'confirmed': return 'Confirmado';
      case 'pending': return 'Pendiente';
      case 'interested': return 'Interesado';
      case 'completed': return 'Completado';
      case 'cancelled': return 'Cancelado';
      default: return 'Desconocido';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed': return FaCheckCircle;
      case 'pending': return FaHourglass;
      case 'interested': return FaHeart;
      case 'completed': return FaCheckCircle;
      case 'cancelled': return FaInfoCircle;
      default: return FaInfoCircle;
    }
  };

  const getStatusAccentLeftColor = (status) => {
    const scheme = status === 'rejecting' ? 'gray' : getStatusColor(status);
    return `${scheme}.500`;
  };

  // Próximos / en curso: aún no finalizados (incluye el tramo entre inicio y fin)
  const eventosProximos = eventos.filter(
    (e) => e.status !== 'cancelled' && !isEventoEfectivamenteFinalizado(e),
  );
  // Pasados / completados: cancelados excluidos; incluye `completed` o fin ya pasado
  const eventosPasados = eventos.filter(
    (e) => e.status !== 'cancelled' && isEventoEfectivamenteFinalizado(e),
  );
  // Eventos cancelados
  const eventosCancelados = eventos.filter(e => e.status === 'cancelled');
  // Para compatibilidad con el código existente
  const eventosCompletados = eventosPasados;

  return (
    <AppLayout userType={userType} userName={getPerfilDisplayName(profile, userType)}>
      <Box py={{ base: 4, md: 8 }}>
        <Container maxW="7xl">
          <VStack gap={{ base: 6, md: 8 }}>
            {/* Header */}
            <Card.Root bg={cardBg} shadow="lg" rounded="2xl" w="full">
              <Card.Header data-tour="eventos-header">
                <Flex justify="space-between" align="start" flexWrap="wrap" gap={4}>
                  <VStack align="start" gap={2} flex={1}>
                    <Heading size="lg">
                      <Icon as={FaCalendarCheck} mr={3} color="purple.600" />
                      Mis eventos
                    </Heading>
                    <Text fontSize="md" color={textColor}>
                      Gestiona tus eventos {userType === 'banda' ? 'con cofradías' : 'con bandas'}
                    </Text>
                  </VStack>
                  
                  {userType === 'cofradia' && (
                    <Button
                      colorPalette="purple"
                      size="lg"
                      rounded="full"
                      onClick={onCreateOpen}
                      fontWeight="600"
                      _hover={{ transform: 'scale(1.02)' }}
                      _active={{ transform: 'scale(0.98)' }}><Icon as={FaPlus} />Crear evento
                                          </Button>
                  )}
                </Flex>
              </Card.Header>
            </Card.Root>

            {/* Estadísticas */}
            <SimpleGrid columns={{ base: 1, md: 3 }} gap={4} w="full" data-tour="eventos-resumen">
              <Card.Root bg="purple.50" shadow="md" rounded="xl">
                <Card.Body textAlign="center">
                  <Icon as={FaCalendarCheck} color="purple.500" w={8} h={8} mb={2} />
                  <Text fontSize="3xl" fontWeight="bold" color="purple.600">
                    {eventos.length}
                  </Text>
                  <Text fontSize="sm" color="purple.700">Total de eventos</Text>
                </Card.Body>
              </Card.Root>
              <Card.Root bg="green.50" shadow="md" rounded="xl">
                <Card.Body textAlign="center">
                  <Icon as={FaCheckCircle} color="green.500" w={8} h={8} mb={2} />
                  <Text fontSize="3xl" fontWeight="bold" color="green.600">
                    {eventosProximos.filter(e => e.status === 'confirmed').length}
                  </Text>
                  <Text fontSize="sm" color="green.700">Eventos confirmados</Text>
                </Card.Body>
              </Card.Root>
              <Card.Root bg="blue.50" shadow="md" rounded="xl">
                <Card.Body textAlign="center">
                  <Icon as={FaMusic} color="blue.500" w={8} h={8} mb={2} />
                  <Text fontSize="3xl" fontWeight="bold" color="blue.600">
                    {eventosCompletados.length}
                  </Text>
                  <Text fontSize="sm" color="blue.700">Eventos completados</Text>
                </Card.Body>
              </Card.Root>
            </SimpleGrid>

            {/* Tabs de eventos */}
            <Card.Root bg={cardBg} shadow="lg" rounded="2xl" w="full">
              <Card.Body>
                <Tabs.Root
                  variant='subtle'
                  colorPalette="purple"
                  value={tabsControlledValue(eventosListTab)}
                  onValueChange={tabsOnValueChange(setEventosListTab)}
                >
                  <Tabs.List mb={6} flexWrap="wrap" gap={2} data-tour="eventos-tabs">
                    <Tab value="0" fontWeight="600">
                      <Icon as={FaCalendarCheck} mr={2} />
                      Próximos ({eventosProximos.length})
                    </Tab>
                    <Tab value="1" fontWeight="600">
                      <Icon as={FaCheckCircle} mr={2} />
                      Pasados ({eventosPasados.length})
                    </Tab>
                    <Tab value="2" fontWeight="600">
                      <Icon as={FaTimes} mr={2} />
                      Cancelados ({eventosCancelados.length})
                    </Tab>
                  </Tabs.List>

                  <TabPanels>
                    {/* Próximos eventos */}
                    <TabPanel value="0" p={0}>
                      <VStack gap={4} align="stretch">
                        {/* Toggle general para hacer públicos todos los eventos */}
                        {userType === 'banda' && eventosProximos.length > 0 && (
                          <Card.Root bg="purple.50" borderWidth="2px" borderColor="purple.200" rounded="lg">
                            <Card.Body py={3}>
                              <Flex justify="space-between" align="center" flexWrap="wrap" gap={3}>
                                <VStack align="start" gap={0} flex={1}>
                                  <HStack gap={2}>
                                    <Icon as={FaEye} color="purple.600" />
                                    <Text fontWeight="600" fontSize="sm" color="purple.900">
                                      Hacer públicos todos los eventos
                                    </Text>
                                  </HStack>
                                  <Text fontSize="xs" color="purple.700">
                                    Los eventos públicos aparecerán en tu perfil para que otros usuarios vean tu actividad
                                  </Text>
                                </VStack>
                                <Switch
                                  colorPalette="purple"
                                  size="lg"
                                  checked={todosPublicos}
                                  onValueChange={handleToggleTodosPublicos}
                                />
                              </Flex>
                            </Card.Body>
                          </Card.Root>
                        )}

                        {/* Selector de ordenación */}
                        <Flex justify="space-between" align="center" flexWrap="wrap" gap={2}>
                          <Text fontSize="sm" color={textColor} fontWeight="medium">
                            Ordenar por:
                          </Text>
                          <NativeSelect.Root>
                            <NativeSelect.Field
                              value={sortBy}
                              onChange={(e) => setSortBy(e.target.value)}
                              size="sm"
                              w={{ base: "full", sm: "250px" }}
                              bg={cardBg}>
                              <option value="date-asc">Fecha (más próximo primero)</option>
                              <option value="date-desc">Fecha (más lejano primero)</option>
                              <option value="status">Estado</option>
                              <option value="name">Nombre del evento</option>
                            </NativeSelect.Field>
                            <NativeSelect.Indicator />
                          </NativeSelect.Root>
                        </Flex>

                        {isLoadingEventos ? (
                          <Center py={12}>
                            <VStack gap={4}>
                              <Spinner size="xl" color="purple.500" borderWidth="4px" />
                              <Text color={textColor}>Cargando eventos...</Text>
                            </VStack>
                          </Center>
                        ) : eventosProximos.length === 0 ? (
                          <VStack gap={4} py={8}>
                            <Icon as={FaCalendarCheck} w={16} h={16} color="gray.300" />
                            <Text fontSize="lg" color={textColor}>
                              No tienes eventos próximos
                            </Text>
                            {userType === 'cofradia' && (
                              <Button colorPalette="purple" onClick={onCreateOpen}><Icon as={FaPlus} />Crear tu primer evento
                                                              </Button>
                            )}
                          </VStack>
                        ) : (
                          sortEvents(eventosProximos)
                            .map((evento) => {
                              const listCardCtx = getProximaEventCardContext(evento, userType, eventosWithActiveAcuerdos);
                              return (
                                <Card.Root 
                                  key={evento.id} 
                                  bg={eventListCardBg} 
                                  shadow="sm"
                                  rounded="lg"
                                  overflow="hidden"
                                  borderWidth="1px"
                                  borderColor={eventCardBorderColor}
                                  borderLeftWidth="4px"
                                  borderLeftColor={getStatusAccentLeftColor(evento.status)}
                                  role="group"
                                  opacity={evento.status === 'rejecting' ? 0 : 1}
                                  transform={evento.status === 'rejecting' ? 'scale(0.95)' : 'scale(1)'}
                                  transition="all 0.5s ease-in-out"
                                  _hover={{
                                    transform: evento.status === 'rejecting' ? 'scale(0.95)' : 'translateY(-2px)',
                                    shadow: 'lg',
                                    borderColor: eventCardHoverBorderColor,
                                    borderLeftColor: getStatusAccentLeftColor(evento.status),
                                  }}
                                >
                                  {!!evento.posterUrl && (
                                    <Box position="relative" h={{ base: '120px', md: '150px' }} overflow="hidden">
                                      <Image
                                        src={evento.posterUrl}
                                        alt={`Cartel ${evento.eventName}`}
                                        w="full"
                                        h="full"
                                        objectFit="cover"
                                        transform="scale(1.02)"
                                        transition="transform 0.25s ease"
                                        _groupHover={{ transform: 'scale(1.08)' }}
                                      />
                                      <Box position="absolute" inset={0} bgGradient={bannerOverlayGradient} />
                                      <HStack position="absolute" top={3} left={3} right={3} justify="space-between">
                                        <HStack gap={2}>
                                          {(evento.photosCount > 0 || evento.videosCount > 0) && (
                                            <Badge
                                              bg="whiteAlpha.800"
                                              color="gray.800"
                                              fontSize="xs"
                                              px={2}
                                              py={1}
                                              rounded="full"
                                              backdropFilter="blur(6px)"
                                            >
                                              {evento.photosCount > 0 ? `📷 ${evento.photosCount}` : ''}
                                              {evento.photosCount > 0 && evento.videosCount > 0 ? '  ·  ' : ''}
                                              {evento.videosCount > 0 ? `▶ ${evento.videosCount}` : ''}
                                            </Badge>
                                          )}
                                        </HStack>
                                        <Badge
                                          colorPalette={getStatusColor(evento.status)}
                                          fontSize="xs"
                                          px={3}
                                          py={1}
                                          rounded="full"
                                        >
                                          <Icon as={getStatusIcon(evento.status)} mr={1} />
                                          {getStatusText(evento.status)}
                                        </Badge>
                                      </HStack>
                                    </Box>
                                  )}
                                  <Card.Body p={{ base: 3, md: 4 }}>
                                    <VStack gap={4} align="stretch">
                                      <Flex align="flex-start" justify="space-between" gap={3} flexWrap="wrap">
                                        <HStack gap={3} align="start" flex={1} minW={{ base: '100%', sm: '200px' }}>
                                          <Avatar.Root
                                            size={{ base: 'sm', md: 'md' }}
                                            flexShrink={0}
                                            bg={
                                              ((evento.status !== 'confirmed' && evento.status !== 'completed') &&
                                                !(userType === 'banda' && eventosWithActiveAcuerdos[evento.id]?.hasAcuerdo)) ||
                                              !evento[otherPartyKey]
                                                ? 'gray.300'
                                                : undefined
                                            }
                                            cursor={
                                              ((evento.status === 'confirmed' || evento.status === 'completed') ||
                                                (userType === 'banda' && eventosWithActiveAcuerdos[evento.id]?.hasAcuerdo)) &&
                                              evento[otherPartyKey]
                                                ? 'pointer'
                                                : 'default'
                                            }
                                            onClick={() =>
                                              ((evento.status === 'confirmed' || evento.status === 'completed') ||
                                                (userType === 'banda' && eventosWithActiveAcuerdos[evento.id]?.hasAcuerdo)) &&
                                              evento[otherPartyKey] &&
                                              handleGoToProfile(evento)
                                            }
                                            _hover={
                                              ((evento.status === 'confirmed' || evento.status === 'completed') ||
                                                (userType === 'banda' && eventosWithActiveAcuerdos[evento.id]?.hasAcuerdo)) &&
                                              evento[otherPartyKey]
                                                ? { opacity: 0.8 }
                                                : {}
                                            }><Avatar.Fallback
                                              name={
                                                ((evento.status === 'confirmed' || evento.status === 'completed') && evento[otherPartyKey]) ||
                                                (userType === 'banda' && eventosWithActiveAcuerdos[evento.id]?.hasAcuerdo && evento[otherPartyKey])
                                                  ? evento[otherPartyKey]
                                                  : 'Sin asignar'
                                              }>{<Icon as={userType === 'banda' ? FaChurch : FaMusic} />}</Avatar.Fallback><Avatar.Image
                                              src={
                                                ((evento.status === 'confirmed' || evento.status === 'completed') ||
                                                  (userType === 'banda' && eventosWithActiveAcuerdos[evento.id]?.hasAcuerdo))
                                                  ? (userType === 'banda' ? evento.cofradiaAvatar : evento.bandaAvatar)
                                                  : undefined
                                              } /></Avatar.Root>
                                          <VStack gap={1} align="start" flex={1} minW={0}>
                                            <Text fontWeight="bold" fontSize="md" lineHeight="short" color="gray.800" _dark={{ color: 'white' }} lineClamp={2}>
                                              {evento.eventName}
                                            </Text>
                                            {((evento.status === 'confirmed' || evento.status === 'completed') ||
                                              (userType === 'banda' && eventosWithActiveAcuerdos[evento.id]?.hasAcuerdo)) &&
                                            evento[otherPartyKey] ? (
                                              <Text
                                                fontSize="sm"
                                                color="purple.600"
                                                fontWeight="600"
                                                cursor="pointer"
                                                _hover={{ textDecoration: 'underline', color: 'purple.700' }}
                                                onClick={() => handleGoToProfile(evento)}
                                              >
                                                {evento[otherPartyKey]}
                                              </Text>
                                            ) : userType === 'cofradia' &&
                                              eventosWithActiveAcuerdos[evento.id]?.hasAcuerdo &&
                                              eventosWithActiveAcuerdos[evento.id]?.acuerdo ? (
                                              <Text fontSize="sm" color="orange.600" fontWeight="500">
                                                Acuerdo propuesto a{' '}
                                                <Text
                                                  as="span"
                                                  color="purple.600"
                                                  fontWeight="600"
                                                  cursor="pointer"
                                                  _hover={{ textDecoration: 'underline', color: 'purple.700' }}
                                                  onClick={() => {
                                                    const acuerdo = eventosWithActiveAcuerdos[evento.id].acuerdo;
                                                    const banda = resolveBandaFromUser(acuerdo?.confirmador);
                                                    if (banda?.id) {
                                                      navigate(`/ver-perfil-banda?id=${banda.id}`);
                                                    }
                                                  }}
                                                >
                                                  {resolveBandaFromUser(eventosWithActiveAcuerdos[evento.id].acuerdo?.confirmador)?.nombre ||
                                                    'Banda'}
                                                </Text>
                                              </Text>
                                            ) : (
                                              <Text fontSize="sm" color={textColor}>
                                                {userType === 'cofradia' ? 'Sin banda asignada' : 'Sin cofradía asignada'}
                                              </Text>
                                            )}
                                          </VStack>
                                        </HStack>
                                        {!evento.posterUrl && (
                                          <Badge
                                            colorPalette={getStatusColor(evento.status)}
                                            fontSize="xs"
                                            px={3}
                                            py={1}
                                            rounded="full"
                                            flexShrink={0}
                                          >
                                            <Icon as={getStatusIcon(evento.status)} mr={1} />
                                            {getStatusText(evento.status)}
                                          </Badge>
                                        )}
                                      </Flex>

                                      <Box
                                        bg={eventCardMetaBg}
                                        borderWidth="1px"
                                        borderColor={eventCardBorderColor}
                                        rounded="lg"
                                        px={3}
                                        py={2.5}
                                      >
                                        <HStack gap={3} flexWrap="wrap" rowGap={2} fontSize="sm" color={textColor}>
                                          <HStack gap={1.5}>
                                            <Icon as={FaCalendarCheck} color="purple.500" />
                                            <Text fontWeight="600">
                                              {new Date(evento.date).toLocaleDateString('es-ES', {
                                                day: 'numeric',
                                                month: 'short',
                                                year: 'numeric',
                                              })}
                                            </Text>
                                          </HStack>
                                          <Text as="span" color="gray.300" display={{ base: 'none', sm: 'inline' }}>
                                            ·
                                          </Text>
                                          <HStack gap={1.5}>
                                            <Icon as={FaClock} color="blue.500" />
                                            <Text fontWeight="600">
                                              {evento.timeEnd
                                                ? `${evento.timeStart || evento.time || ''} – ${evento.timeEnd}`
                                                : evento.timeStart || evento.time || '—'}
                                            </Text>
                                          </HStack>
                                        </HStack>
                                      </Box>

                                      {evento.notes && (
                                        <Box
                                          bg={notesHighlightBg}
                                          px={3}
                                          py={2}
                                          rounded="md"
                                          borderLeftWidth="3px"
                                          borderColor="blue.400"
                                        >
                                          <HStack gap={2} align="start">
                                            <Icon as={FaInfoCircle} color="blue.500" mt={0.5} flexShrink={0} />
                                            <Text fontSize="sm" color={textColor} lineClamp={4}>
                                              {evento.notes}
                                            </Text>
                                          </HStack>
                                        </Box>
                                      )}

                                      {userType === 'banda' && (evento.status === 'confirmed' || evento.status === 'completed') && (
                                        <Flex
                                          bg={eventosPublicos.includes(evento.id) ? 'green.50' : 'gray.100'}
                                          _dark={{
                                            bg: eventosPublicos.includes(evento.id) ? 'green.900' : 'whiteAlpha.100',
                                          }}
                                          px={3}
                                          py={2}
                                          rounded="md"
                                          borderWidth="1px"
                                          borderColor={eventosPublicos.includes(evento.id) ? 'green.200' : 'gray.200'}
                                          align="center"
                                          justify="space-between"
                                          gap={3}
                                        >
                                          <HStack gap={2} flex={1} minW={0}>
                                            <Icon
                                              as={FaEye}
                                              color={eventosPublicos.includes(evento.id) ? 'green.600' : 'gray.500'}
                                              flexShrink={0}
                                            />
                                            <Text fontSize="sm" fontWeight="600" lineClamp={2}>
                                              {eventosPublicos.includes(evento.id)
                                                ? 'Visible en tu perfil público'
                                                : 'Solo visible para ti (privado)'}
                                            </Text>
                                          </HStack>
                                          <Switch
                                            colorPalette="green"
                                            checked={eventosPublicos.includes(evento.id)}
                                            onValueChange={() => handleToggleEventoPublico(evento.id)}
                                          />
                                        </Flex>
                                      )}

                                      {listCardCtx && (
                                        <Alert.Root
                                          status={listCardCtx.status}
                                          variant='subtle'
                                          borderRadius="md"
                                          py={2}
                                          fontSize="sm"
                                          borderStartWidth='3px'
                                          borderStartColor='colorPalette.solid'>
                                          <Alert.Indicator />
                                          <Alert.Description mb={0} fontSize="sm">
                                            {listCardCtx.text}
                                          </Alert.Description>
                                        </Alert.Root>
                                      )}

                                      <Box
                                        borderTopWidth="1px"
                                        borderColor={eventCardBorderColor}
                                        bg={eventCardFooterBg}
                                        mx={{ base: -3, md: -4 }}
                                        mb={{ base: -3, md: -4 }}
                                        mt={1}
                                        px={{ base: 3, md: 4 }}
                                        py={3}
                                        roundedBottom="lg"
                                      >
                                        <Flex align="center" justify="space-between" gap={3} flexWrap="wrap">
                                          <Wrap gap={2}>
                                            <WrapItem>
                                              <Button
                                                size="sm"
                                                colorPalette="purple"
                                                variant="solid"
                                                onClick={() => handleViewDetails(evento)}
                                                {...eventCardActionButtonProps}
                                              >
                                                Ver detalles
                                              </Button>
                                            </WrapItem>
                                            {userType === 'cofradia' &&
                                              evento.status === 'pending' &&
                                              !eventosWithActiveAcuerdos[evento.id]?.hasAcuerdo && (
                                                <WrapItem>
                                                  <Button
                                                    size="sm"
                                                    colorPalette="green"
                                                    variant="outline"
                                                    onClick={() => handleSearchBanda(evento)}
                                                    {...eventCardActionButtonProps}><Icon as={FaSearch} />Buscar banda
                                                                                                      </Button>
                                                </WrapItem>
                                              )}
                                            {userType === 'banda' && evento.status === 'pending' && (
                                              <>
                                                {!eventosWithActiveAcuerdos[evento.id]?.hasAcuerdo && (
                                                  <>
                                                    <WrapItem>
                                                      <Button
                                                        size="sm"
                                                        colorPalette="green"
                                                        variant="outline"
                                                        onClick={() => handleShowInterest(evento)}
                                                        {...eventCardActionButtonProps}><Icon as={FaHeart} />Me interesa
                                                                                                              </Button>
                                                    </WrapItem>
                                                    <WrapItem>
                                                      <Button
                                                        size="sm"
                                                        colorPalette="red"
                                                        variant="outline"
                                                        onClick={() => handleRejectEvent(evento)}
                                                        {...eventCardActionButtonProps}><Icon as={FaTimes} />No me interesa
                                                                                                              </Button>
                                                    </WrapItem>
                                                  </>
                                                )}
                                              </>
                                            )}
                                            {userType === 'banda' && evento.status === 'interested' && (
                                              <WrapItem>
                                                <Tooltip
                                                  content="Esperando a que la cofradía proponga un acuerdo. Podrás aceptarlo o rechazarlo desde el detalle del evento."
                                                  showArrow
                                                  positioning={{
                                                    placement: "top"
                                                  }}><span>
                                                    <Button
                                                      size="sm"
                                                      colorPalette="purple"
                                                      variant="outline"
                                                      disabled
                                                      {...eventCardActionButtonProps}><Icon as={FaHourglass} />Esperando propuesta
                                                                                                        </Button>
                                                  </span></Tooltip>
                                              </WrapItem>
                                            )}
                                          </Wrap>
                                          {userType === 'cofradia' &&
                                            evento.status !== 'completed' &&
                                            evento.status !== 'cancelled' && (
                                              <Tooltip
                                                content="Cancela el acto y borra la información. No se puede deshacer."
                                                showArrow
                                              >
                                                <Button
                                                  size="sm"
                                                  variant="ghost"
                                                  colorPalette="red"
                                                  onClick={() => handleOpenDeleteModal(evento)}><Icon as={FaTrash} />Cancelar evento
                                                                                                  </Button>
                                              </Tooltip>
                                            )}
                                        </Flex>
                                      </Box>
                                    </VStack>
                                  </Card.Body>
                                </Card.Root>
                              );
                            })
                        )}
                      </VStack>
                    </TabPanel>

                    {/* Eventos completados */}
                    <TabPanel value="1" p={0}>
                      <VStack gap={4} align="stretch">
                        {/* Selector de ordenación */}
                        <Flex justify="space-between" align="center" flexWrap="wrap" gap={2}>
                          <Text fontSize="sm" color={textColor} fontWeight="medium">
                            Ordenar por:
                          </Text>
                          <NativeSelect.Root>
                            <NativeSelect.Field
                              value={sortBy}
                              onChange={(e) => setSortBy(e.target.value)}
                              size="sm"
                              w={{ base: "full", sm: "250px" }}
                              bg={cardBg}>
                              <option value="date-asc">Fecha (más antiguo primero)</option>
                              <option value="date-desc">Fecha (más reciente primero)</option>
                              <option value="status">Estado</option>
                              <option value="name">Nombre del evento</option>
                            </NativeSelect.Field>
                            <NativeSelect.Indicator />
                          </NativeSelect.Root>
                        </Flex>

                        {eventosCompletados.length === 0 ? (
                          <VStack gap={4} py={8}>
                            <Icon as={FaCheckCircle} w={16} h={16} color="gray.300" />
                            <Text fontSize="lg" color={textColor}>
                              No tienes eventos completados
                            </Text>
                          </VStack>
                        ) : (
                          sortEvents(eventosCompletados)
                            .map((evento) => (
                              <Card.Root
                                key={evento.id}
                                bg={eventListCardBg}
                                shadow="sm"
                                rounded="lg"
                                overflow="hidden"
                                borderWidth="1px"
                                borderColor={eventCardBorderColor}
                                borderLeftWidth="4px"
                                borderLeftColor={getStatusAccentLeftColor(evento.status)}
                                role="group"
                                transition="all 0.2s ease"
                                _hover={{
                                  transform: 'translateY(-2px)',
                                  shadow: 'lg',
                                  borderColor: eventCardHoverBorderColor,
                                  borderLeftColor: getStatusAccentLeftColor(evento.status),
                                }}
                              >
                                {!!evento.posterUrl && (
                                  <Box position="relative" h={{ base: '120px', md: '150px' }} overflow="hidden">
                                    <Image
                                      src={evento.posterUrl}
                                      alt={`Cartel ${evento.eventName}`}
                                      w="full"
                                      h="full"
                                      objectFit="cover"
                                      transform="scale(1.02)"
                                      transition="transform 0.25s ease"
                                      _groupHover={{ transform: 'scale(1.08)' }}
                                    />
                                    <Box position="absolute" inset={0} bgGradient={bannerOverlayGradient} />
                                    <HStack position="absolute" top={3} left={3} right={3} justify="space-between">
                                      <HStack gap={2}>
                                        {(evento.photosCount > 0 || evento.videosCount > 0) && (
                                          <Badge
                                            bg="whiteAlpha.800"
                                            color="gray.800"
                                            fontSize="xs"
                                            px={2}
                                            py={1}
                                            rounded="full"
                                            backdropFilter="blur(6px)"
                                          >
                                            {evento.photosCount > 0 ? `📷 ${evento.photosCount}` : ''}
                                            {evento.photosCount > 0 && evento.videosCount > 0 ? '  ·  ' : ''}
                                            {evento.videosCount > 0 ? `▶ ${evento.videosCount}` : ''}
                                          </Badge>
                                        )}
                                      </HStack>
                                      <Badge
                                        colorPalette={getStatusColor(evento.status)}
                                        fontSize="xs"
                                        px={3}
                                        py={1}
                                        rounded="full"
                                      >
                                        <Icon as={getStatusIcon(evento.status)} mr={1} />
                                        {getStatusText(evento.status)}
                                      </Badge>
                                    </HStack>
                                  </Box>
                                )}
                                <Card.Body p={{ base: 3, md: 4 }}>
                                  <VStack gap={4} align="stretch">
                                    <Flex align="flex-start" justify="space-between" gap={3} flexWrap="wrap">
                                      <HStack gap={3} align="start" flex={1} minW={{ base: '100%', sm: '200px' }}>
                                        <Avatar.Root
                                          size={{ base: 'sm', md: 'md' }}
                                          flexShrink={0}
                                          bg={
                                            (evento.status !== 'confirmed' && evento.status !== 'completed') || !evento[otherPartyKey]
                                              ? 'gray.300'
                                              : undefined
                                          }
                                          cursor={
                                            (evento.status === 'confirmed' || evento.status === 'completed') && evento[otherPartyKey]
                                              ? 'pointer'
                                              : 'default'
                                          }
                                          onClick={() =>
                                            (evento.status === 'confirmed' || evento.status === 'completed') &&
                                            evento[otherPartyKey] &&
                                            handleGoToProfile(evento)
                                          }
                                          _hover={
                                            (evento.status === 'confirmed' || evento.status === 'completed') && evento[otherPartyKey]
                                              ? { opacity: 0.8 }
                                              : {}
                                          }><Avatar.Fallback
                                            name={
                                              (evento.status === 'confirmed' || evento.status === 'completed') && evento[otherPartyKey]
                                                ? evento[otherPartyKey]
                                                : 'Sin asignar'
                                            }>{<Icon as={userType === 'banda' ? FaChurch : FaMusic} />}</Avatar.Fallback><Avatar.Image
                                            src={
                                              (evento.status === 'confirmed' || evento.status === 'completed')
                                                ? (userType === 'banda' ? evento.cofradiaAvatar : evento.bandaAvatar)
                                                : undefined
                                            } /></Avatar.Root>
                                        <VStack gap={1} align="start" flex={1} minW={0}>
                                          <Text
                                            fontWeight="bold"
                                            fontSize="md"
                                            lineHeight="short"
                                            color="gray.800"
                                            _dark={{ color: 'white' }}
                                            lineClamp={2}
                                          >
                                            {evento.eventName}
                                          </Text>
                                          {(evento.status === 'confirmed' || evento.status === 'completed') &&
                                          evento[otherPartyKey] ? (
                                            <Text
                                              fontSize="sm"
                                              color="purple.600"
                                              fontWeight="600"
                                              cursor="pointer"
                                              _hover={{ textDecoration: 'underline', color: 'purple.700' }}
                                              onClick={() => handleGoToProfile(evento)}
                                            >
                                              {evento[otherPartyKey]}
                                            </Text>
                                          ) : (
                                            <Text fontSize="sm" color={textColor}>
                                              {userType === 'cofradia' ? 'Sin banda asignada' : 'Sin cofradía asignada'}
                                            </Text>
                                          )}
                                        </VStack>
                                      </HStack>
                                      {!evento.posterUrl && (
                                        <Badge
                                          colorPalette={getStatusColor(evento.status)}
                                          fontSize="xs"
                                          px={3}
                                          py={1}
                                          rounded="full"
                                          flexShrink={0}
                                        >
                                          <Icon as={getStatusIcon(evento.status)} mr={1} />
                                          {getStatusText(evento.status)}
                                        </Badge>
                                      )}
                                    </Flex>

                                    <Box
                                      bg={eventCardMetaBg}
                                      borderWidth="1px"
                                      borderColor={eventCardBorderColor}
                                      rounded="lg"
                                      px={3}
                                      py={2.5}
                                    >
                                      <HStack gap={3} flexWrap="wrap" rowGap={2} fontSize="sm" color={textColor}>
                                        <HStack gap={1.5}>
                                          <Icon as={FaCalendarCheck} color="purple.500" />
                                          <Text fontWeight="600">
                                            {new Date(evento.date).toLocaleDateString('es-ES', {
                                              day: 'numeric',
                                              month: 'short',
                                              year: 'numeric',
                                            })}
                                          </Text>
                                        </HStack>
                                        <Text as="span" color="gray.300" display={{ base: 'none', sm: 'inline' }}>
                                          ·
                                        </Text>
                                        <HStack gap={1.5}>
                                          <Icon as={FaClock} color="blue.500" />
                                          <Text fontWeight="600">
                                            {evento.timeEnd
                                              ? `${evento.timeStart || evento.time || ''} – ${evento.timeEnd}`
                                              : evento.timeStart || evento.time || '—'}
                                          </Text>
                                        </HStack>
                                      </HStack>
                                    </Box>

                                    {evento.notes && (
                                      <Box
                                        bg={notesHighlightBg}
                                        px={3}
                                        py={2}
                                        rounded="md"
                                        borderLeftWidth="3px"
                                        borderColor="blue.400"
                                      >
                                        <HStack gap={2} align="start">
                                          <Icon as={FaInfoCircle} color="blue.500" mt={0.5} flexShrink={0} />
                                          <Text fontSize="sm" color={textColor} lineClamp={4}>
                                            {evento.notes}
                                          </Text>
                                        </HStack>
                                      </Box>
                                    )}

                                    <Box
                                      borderTopWidth="1px"
                                      borderColor={eventCardBorderColor}
                                      bg={eventCardFooterBg}
                                      mx={{ base: -3, md: -4 }}
                                      mb={{ base: -3, md: -4 }}
                                      mt={1}
                                      px={{ base: 3, md: 4 }}
                                      py={3}
                                      roundedBottom="lg"
                                    >
                                      <Wrap gap={2}>
                                        <WrapItem>
                                          <Button
                                            size="sm"
                                            colorPalette="purple"
                                            variant="solid"
                                            onClick={() => handleViewDetails(evento)}
                                            {...eventCardActionButtonProps}
                                          >
                                            Ver detalles
                                          </Button>
                                        </WrapItem>
                                        {evento[otherPartyKey] && !eventosConResena.includes(evento.id) && (
                                          <WrapItem>
                                            <Button
                                              size="sm"
                                              colorPalette="blue"
                                              variant="solid"
                                              onClick={() => handleReviewFromEvents(evento)}
                                              {...eventCardActionButtonProps}><Icon as={FaComments} />Dejar reseña
                                                                                          </Button>
                                          </WrapItem>
                                        )}
                                      </Wrap>
                                    </Box>
                                  </VStack>
                                </Card.Body>
                              </Card.Root>
                            ))
                        )}
                      </VStack>
                    </TabPanel>

                    {/* Eventos cancelados */}
                    <TabPanel value="2" p={0}>
                      <VStack gap={4} align="stretch">
                        {eventosCancelados.length === 0 ? (
                          <VStack gap={4} py={8}>
                            <Icon as={FaTimes} w={16} h={16} color="gray.300" />
                            <Text fontSize="lg" color={textColor}>
                              No tienes eventos cancelados
                            </Text>
                          </VStack>
                        ) : (
                          eventosCancelados.map((evento) => (
                            <Card.Root
                              key={evento.id}
                              bg={cancelledEventCardBg}
                              shadow="sm"
                              rounded="lg"
                              overflow="hidden"
                              borderColor="red.200"
                              borderWidth="1px"
                              borderLeftWidth="4px"
                              borderLeftColor={getStatusAccentLeftColor(evento.status)}
                              role="group"
                              transition="all 0.2s ease"
                              _hover={{
                                transform: 'translateY(-2px)',
                                shadow: 'lg',
                                borderColor: 'red.300',
                                borderLeftColor: getStatusAccentLeftColor(evento.status),
                              }}
                            >
                              {!!evento.posterUrl && (
                                <Box position="relative" h={{ base: '120px', md: '150px' }} overflow="hidden">
                                  <Image
                                    src={evento.posterUrl}
                                    alt={`Cartel ${evento.eventName}`}
                                    w="full"
                                    h="full"
                                    objectFit="cover"
                                    transform="scale(1.02)"
                                    transition="transform 0.25s ease"
                                    _groupHover={{ transform: 'scale(1.08)' }}
                                  />
                                  <Box position="absolute" inset={0} bgGradient={bannerOverlayGradient} />
                                  <HStack position="absolute" top={3} left={3} right={3} justify="flex-end">
                                    <Badge
                                      colorPalette="red"
                                      fontSize="xs"
                                      px={3}
                                      py={1}
                                      rounded="full"
                                    >
                                      <Icon as={FaTimes} mr={1} />
                                      Cancelado
                                    </Badge>
                                  </HStack>
                                </Box>
                              )}
                              <Card.Body p={{ base: 3, md: 4 }}>
                                <VStack gap={4} align="stretch">
                                  <Flex align="flex-start" justify="space-between" gap={3} flexWrap="wrap">
                                    <HStack gap={3} align="start" flex={1} minW={{ base: '100%', sm: '200px' }}>
                                      <Avatar.Root
                                        size={{ base: 'sm', md: 'md' }}
                                        flexShrink={0}
                                        bg={!evento[otherPartyKey] ? 'gray.300' : undefined}
                                        cursor={evento[otherPartyKey] ? 'pointer' : 'default'}
                                        onClick={() => evento[otherPartyKey] && handleGoToProfile(evento)}
                                        _hover={evento[otherPartyKey] ? { opacity: 0.8 } : {}}><Avatar.Fallback name={evento[otherPartyKey] || 'Sin asignar'}>{<Icon as={userType === 'banda' ? FaChurch : FaMusic} />}</Avatar.Fallback><Avatar.Image src={userType === 'banda' ? evento.cofradiaAvatar : evento.bandaAvatar} /></Avatar.Root>
                                      <VStack gap={1} align="start" flex={1} minW={0}>
                                        <Text
                                          fontWeight="bold"
                                          fontSize="md"
                                          lineHeight="short"
                                          color="gray.800"
                                          _dark={{ color: 'white' }}
                                          lineClamp={2}
                                        >
                                          {evento.eventName}
                                        </Text>
                                        {evento[otherPartyKey] ? (
                                          <Text
                                            fontSize="sm"
                                            color="purple.600"
                                            fontWeight="600"
                                            cursor="pointer"
                                            _hover={{ textDecoration: 'underline', color: 'purple.700' }}
                                            onClick={() => handleGoToProfile(evento)}
                                          >
                                            {evento[otherPartyKey]}
                                          </Text>
                                        ) : (
                                          <Text fontSize="sm" color={textColor}>
                                            {userType === 'cofradia' ? 'Sin banda asignada' : 'Sin cofradía asignada'}
                                          </Text>
                                        )}
                                      </VStack>
                                    </HStack>
                                    {!evento.posterUrl && (
                                      <Badge
                                        colorPalette="red"
                                        fontSize="xs"
                                        px={3}
                                        py={1}
                                        rounded="full"
                                        flexShrink={0}
                                      >
                                        <Icon as={FaTimes} mr={1} />
                                        Cancelado
                                      </Badge>
                                    )}
                                  </Flex>

                                  <Box
                                    bg={eventCardMetaBg}
                                    borderWidth="1px"
                                    borderColor={eventCardBorderColor}
                                    rounded="lg"
                                    px={3}
                                    py={2.5}
                                  >
                                    <HStack gap={3} flexWrap="wrap" rowGap={2} fontSize="sm" color={textColor}>
                                      <HStack gap={1.5}>
                                        <Icon as={FaCalendarCheck} color="purple.500" />
                                        <Text fontWeight="600">
                                          {new Date(evento.date).toLocaleDateString('es-ES', {
                                            day: 'numeric',
                                            month: 'short',
                                            year: 'numeric',
                                          })}
                                        </Text>
                                      </HStack>
                                      <Text as="span" color="gray.300" display={{ base: 'none', sm: 'inline' }}>
                                        ·
                                      </Text>
                                      <HStack gap={1.5}>
                                        <Icon as={FaClock} color="blue.500" />
                                        <Text fontWeight="600">
                                          {evento.timeEnd
                                            ? `${evento.timeStart || evento.time || ''} – ${evento.timeEnd}`
                                            : evento.timeStart || evento.time || '—'}
                                        </Text>
                                      </HStack>
                                    </HStack>
                                  </Box>

                                  {evento.cancelReason && (
                                    <Alert.Root
                                      status="warning"
                                      variant='subtle'
                                      borderRadius="md"
                                      py={2}
                                      fontSize="sm"
                                      borderStartWidth='3px'
                                      borderStartColor='colorPalette.solid'>
                                      <Alert.Indicator />
                                      <Box>
                                        <Alert.Title fontSize="sm" fontWeight="bold">
                                          Motivo de cancelación
                                        </Alert.Title>
                                        <Alert.Description fontSize="sm" mb={0}>
                                          {evento.cancelReason}
                                        </Alert.Description>
                                      </Box>
                                    </Alert.Root>
                                  )}

                                  {evento.notes && (
                                    <Box
                                      bg={notesHighlightBg}
                                      px={3}
                                      py={2}
                                      rounded="md"
                                      borderLeftWidth="3px"
                                      borderColor="blue.400"
                                    >
                                      <HStack gap={2} align="start">
                                        <Icon as={FaInfoCircle} color="blue.500" mt={0.5} flexShrink={0} />
                                        <Text fontSize="sm" color={textColor} lineClamp={4}>
                                          {evento.notes}
                                        </Text>
                                      </HStack>
                                    </Box>
                                  )}

                                  <Box
                                    borderTopWidth="1px"
                                    borderColor={eventCardBorderColor}
                                    bg={eventCardFooterBg}
                                    mx={{ base: -3, md: -4 }}
                                    mb={{ base: -3, md: -4 }}
                                    mt={1}
                                    px={{ base: 3, md: 4 }}
                                    py={3}
                                    roundedBottom="lg"
                                  >
                                    <Wrap gap={2}>
                                      <WrapItem>
                                        <Button
                                          size="sm"
                                          colorPalette="purple"
                                          variant="solid"
                                          onClick={() => handleViewDetails(evento)}
                                          {...eventCardActionButtonProps}
                                        >
                                          Ver detalles
                                        </Button>
                                      </WrapItem>
                                      {eventosWithConversations.has(evento.id) && (
                                        <WrapItem>
                                          <Button
                                            size="sm"
                                            colorPalette="blue"
                                            variant="outline"
                                            onClick={() => handleNavigateToEventChat(evento)}
                                            {...eventCardActionButtonProps}><Icon as={FaEnvelope} />Ver conversaciones
                                                                                      </Button>
                                        </WrapItem>
                                      )}
                                    </Wrap>
                                  </Box>
                                </VStack>
                              </Card.Body>
                            </Card.Root>
                          ))
                        )}
                      </VStack>
                    </TabPanel>
                  </TabPanels>
                </Tabs.Root>
              </Card.Body>
            </Card.Root>
          </VStack>
        </Container>
      </Box>

      {/* Modal de detalles del evento */}
      <Dialog.Root open={isOpen} size='xl' onOpenChange={e => {
        if (!e.open) {
          onClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content maxH="90vh">
              <Dialog.Header>
                <VStack gap={2} align="start">
                  <HStack justify="space-between" w="full">
                    <Text>Evento: {selectedEvent?.eventName}</Text>
                    {selectedEvent && (
                      <Badge
                        colorPalette={getStatusColor(selectedEvent.status)}
                        fontSize="xs"
                        px={3}
                        py={1}
                        rounded="full"
                      >
                        <Icon as={getStatusIcon(selectedEvent.status)} mr={1} />
                        {getStatusText(selectedEvent.status)}
                      </Badge>
                    )}
                  </HStack>
                </VStack>
              </Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body pb={6} overflow="auto">
                <Tabs.Root value={tabsControlledValue(activeTabInModal)} onValueChange={tabsOnValueChange(setActiveTabInModal)} variant='subtle' colorPalette="purple">
                  <Tabs.List mb={4} gap={2}>
                    <Tab value="0" fontWeight="600">
                      <Icon as={FaInfoCircle} mr={2} />
                      Detalles
                    </Tab>
                    {userType === 'cofradia' && selectedEvent?.status === 'pending' && !selectedEvent[otherPartyKey] && (
                      <Tab value="1" fontWeight="600">
                        <Icon as={FaSearch} mr={2} />
                        Buscar banda
                      </Tab>
                    )}
                  </Tabs.List>

                  <TabPanels>
                    {/* Tab 1: Detalles del evento */}
                    <TabPanel value="0" px={0}>
                {selectedEvent && (
                  <VStack gap={4} align="stretch">
                    {/* Información principal */}
                    <HStack gap={3}>
                      <Avatar.Root size="lg"><Avatar.Fallback name={selectedEvent[otherPartyKey]}>{<Icon as={userType === 'banda' ? FaChurch : FaMusic} />}</Avatar.Fallback></Avatar.Root>
                      <VStack gap={0} align="start" flex={1}>
                        <Text fontWeight="bold" fontSize="lg">
                          {selectedEvent.eventName}
                        </Text>
                        <Text fontSize="md" color={textColor}>
                          {selectedEvent[otherPartyKey]}
                        </Text>
                      </VStack>
                    </HStack>

                    <Separator />

                    {/* Detalles completos en dos columnas */}
                    <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
                      <HStack gap={3}>
                        <Icon as={FaCalendarCheck} color="purple.500" w={5} h={5} />
                        <VStack gap={0} align="start" flex={1}>
                          <Text fontSize="xs" color={textColor} fontWeight="medium">Fecha</Text>
                          <Text fontSize="md">
                            {new Date(selectedEvent.date).toLocaleDateString('es-ES', {
                              weekday: 'long',
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            })}
                          </Text>
                        </VStack>
                      </HStack>

                      <HStack gap={3}>
                        <Icon as={FaClock} color="blue.500" w={5} h={5} />
                        <VStack gap={0} align="start" flex={1}>
                          <Text fontSize="xs" color={textColor} fontWeight="medium">Hora de inicio</Text>
                          <Text fontSize="md">{selectedEvent.time}</Text>
                        </VStack>
                      </HStack>

                      <HStack gap={3}>
                        <Icon as={FaClock} color="orange.500" w={5} h={5} />
                        <VStack gap={0} align="start" flex={1}>
                          <Text fontSize="xs" color={textColor} fontWeight="medium">Duración</Text>
                          <Text fontSize="md">{selectedEvent.duration}</Text>
                        </VStack>
                      </HStack>

                      <HStack gap={3}>
                        <Icon as={FaMapMarkerAlt} color="red.500" w={5} h={5} />
                        <VStack gap={0} align="start" flex={1}>
                          <Text fontSize="xs" color={textColor} fontWeight="medium">Ubicación</Text>
                          <Text fontSize="md">{selectedEvent.location}</Text>
                        </VStack>
                      </HStack>
                    </SimpleGrid>

                    {/* Notas */}
                    {selectedEvent.notes && (
                      <>
                        <Separator />
                        <Box>
                          <Text fontSize="xs" color={textColor} fontWeight="medium" mb={2}>
                            Notas adicionales
                          </Text>
                          <Box
                            bg={notesHighlightBg}
                            p={3}
                            rounded="md"
                            borderLeft="4px solid"
                            borderColor="blue.400"
                          >
                            <Text fontSize="sm">{selectedEvent.notes}</Text>
                          </Box>
                        </Box>
                      </>
                    )}

                    {/* Bandas preseleccionadas en el modal de detalles */}
                    {userType === 'cofradia' && eventShortlists[selectedEvent.id] && eventShortlists[selectedEvent.id].length > 0 && (
                      <>
                        <Separator />
                        <Box>
                          <HStack gap={2} mb={3}>
                            <Icon as={FaHeart} color="yellow.500" w={4} h={4} />
                            <Text fontSize="sm" fontWeight="bold" color="yellow.700">
                              Bandas favoritas ({eventShortlists[selectedEvent.id].length})
                            </Text>
                          </HStack>
                          
                          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={3} w="full">
                            {eventShortlists[selectedEvent.id].map((banda) => (
                              <Card.Root 
                                key={banda.id}
                                bg={shortlistCardBg}
                                borderWidth="2px"
                                borderColor="yellow.400"
                                position="relative"
                                transition="all 0.2s"
                                _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
                              >
                                {!banda.conversacionId && (
                                  <Tooltip content="Quitar de favoritas">
                                    <IconButton
                                      position="absolute"
                                      top={2}
                                      right={2}
                                      size="xs"
                                      variant="ghost"
                                      colorPalette="red"
                                      onClick={() => handleRemoveFromShortlist(selectedEvent.id, banda.id)}
                                      aria-label="Quitar de favoritas"
                                      zIndex={1}><FaTimes /></IconButton>
                                  </Tooltip>
                                )}
                                <Card.Body p={4}>
                                  <VStack gap={3} align="stretch">
                                    <HStack gap={3}>
                                      <Avatar.Root size="md"><Avatar.Fallback name={banda.name}>{<Icon as={FaMusic} />}</Avatar.Fallback><Avatar.Image src={banda.avatar} /></Avatar.Root>
                                      <VStack align="start" gap={0} flex={1} minW={0}>
                                        <Text fontWeight="bold" fontSize="sm" lineClamp={1}>
                                          {banda.name}
                                        </Text>
                                        <HStack gap={1}>
                                          <Icon as={FaMapMarkerAlt} color="gray.500" boxSize={3} />
                                          <Text fontSize="xs" color={textColor} lineClamp={1}>
                                            {banda.location}
                                          </Text>
                                        </HStack>
                                      </VStack>
                                    </HStack>
                                    
                                    <VStack gap={2} w="full">
                                      <Button
                                        size="sm"
                                        colorPalette="purple"
                                        w="full"
                                        onClick={() => handleSelectBandaForAcuerdo(banda, selectedEvent)}><Icon as={FaHandshake} boxSize={3} />Proponer acuerdo
                                                                          </Button>
                                      <Button
                                        size="sm"
                                        colorPalette="blue"
                                        variant="outline"
                                        w="full"
                                        onClick={async () => {
                                          onClose();
                                          try {
                                            // Buscar o crear conversación para este evento y banda
                                            const response = await mensajesAPI.createConversacion(
                                              banda.userId,
                                              selectedEvent.id
                                            );
                                            const conversacionId = response.data.id;
                                            // Navegar directamente a esa conversación
                                            navigate(`${routePrefix}/mensajes?conversacion=${conversacionId}`);
                                          } catch (error) {
                                            // Si hay error, navegar a mensajes sin conversación específica
                                            console.error('Error abriendo conversación:', error);
                                            navigate(`${routePrefix}/mensajes`);
                                          }
                                        }}><Icon as={FaEnvelope} boxSize={3} />Mensaje
                                                                          </Button>
                                    </VStack>
                                  </VStack>
                                </Card.Body>
                              </Card.Root>
                            ))}
                          </SimpleGrid>
                        </Box>
                      </>
                    )}
                  </VStack>
                )}
                    </TabPanel>

                    {/* Tab 2: Buscar banda */}
                    {userType === 'cofradia' && selectedEvent?.status === 'pending' && !selectedEvent[otherPartyKey] && (
                      <TabPanel value="1" px={0}>
                        <VStack gap={6} align="stretch">
                          {/* Shortlist de bandas */}
                          {selectedEvent && eventShortlists[selectedEvent.id] && eventShortlists[selectedEvent.id].length > 0 && (
                            <Box>
                              <Alert.Root status="info" rounded="xl" mb={4}>
                                <Alert.Indicator />
                                <Box flex="1">
                                  <Alert.Title>Bandas favoritas ({eventShortlists[selectedEvent.id].length})</Alert.Title>
                                  <Alert.Description>
                                    Has marcado {eventShortlists[selectedEvent.id].length} banda(s) como favoritas. 
                                    Ve a la pestaña "Detalles" para proponer un acuerdo a una de ellas.
                                  </Alert.Description>
                                </Box>
                              </Alert.Root>
                              
                              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={3} mb={6}>
                                {eventShortlists[selectedEvent.id].map((banda) => (
                                  <Card.Root 
                                    key={banda.id}
                                    bg={shortlistCardBg}
                                    borderWidth="2px"
                                    borderColor="yellow.400"
                                    position="relative"
                                    transition="all 0.2s"
                                    _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
                                  >
                                    {!banda.conversacionId && (
                                      <Tooltip content="Quitar de favoritas">
                                        <IconButton
                                          position="absolute"
                                          top={2}
                                          right={2}
                                          size="xs"
                                          variant="ghost"
                                          colorPalette="red"
                                          onClick={() => handleRemoveFromShortlist(selectedEvent.id, banda.id)}
                                          aria-label="Quitar de favoritas"
                                          zIndex={1}><FaTimes /></IconButton>
                                      </Tooltip>
                                    )}
                                    <Card.Body p={4}>
                                      <VStack gap={3} align="stretch">
                                        <HStack gap={3}>
                                          <Avatar.Root size="md"><Avatar.Fallback name={banda.name}>{<Icon as={FaMusic} />}</Avatar.Fallback><Avatar.Image src={banda.avatar} /></Avatar.Root>
                                          <VStack align="start" gap={0} flex={1} minW={0}>
                                            <Text fontWeight="bold" fontSize="sm" lineClamp={1}>
                                              {banda.name}
                                            </Text>
                                            <HStack gap={1}>
                                              <Icon as={FaMapMarkerAlt} color="gray.500" boxSize={3} />
                                              <Text fontSize="xs" color={textColor} lineClamp={1}>
                                                {banda.location}
                                              </Text>
                                            </HStack>
                                            <HStack gap={1}>
                                              <Icon as={FaStar} color="yellow.500" boxSize={3} />
                                              <Text fontSize="xs" color={textColor}>
                                                {banda.trustedBy} confían
                                              </Text>
                                            </HStack>
                                          </VStack>
                                        </HStack>
                                        
                                        <Button
                                          size="sm"
                                          colorPalette="purple"
                                          variant="outline"
                                          w="full"
                                          onClick={() => navigate(`/ver-perfil-banda?id=${banda.id}`)}
                                        >
                                          Ver perfil
                                        </Button>
                                        <Button
                                          size="sm"
                                          colorPalette="blue"
                                          w="full"
                                          onClick={async () => {
                                            if (!selectedEvent?.id || !banda.userId) return;
                                            if (banda.conversacionId) {
                                              navigate(
                                                `${routePrefix}/mensajes?conversacion=${banda.conversacionId}`,
                                              );
                                              return;
                                            }
                                            try {
                                              const response = await mensajesAPI.createConversacion(
                                                banda.userId,
                                                selectedEvent.id,
                                              );
                                              navigate(
                                                `${routePrefix}/mensajes?conversacion=${response.data.id}`,
                                              );
                                            } catch (error) {
                                              console.error('Error abriendo conversación:', error);
                                              navigate(`${routePrefix}/mensajes`);
                                            }
                                          }}><Icon as={FaEnvelope} boxSize={3} />Contactar
                                                                              </Button>
                                      </VStack>
                                    </Card.Body>
                                  </Card.Root>
                                ))}
                              </SimpleGrid>
                              
                              <Separator mb={6} />
                            </Box>
                          )}
                          
                          {/* Componente de búsqueda */}
                          <Search 
                            userType="cofradia"
                            inModal={true}
                            eventDate={selectedEvent?.date}
                            eventName={selectedEvent?.eventName || selectedEvent?.nombre}
                            eventLocation={selectedEvent?.locationStart || selectedEvent?.location || selectedEvent?.ubicacionSalida || selectedEvent?.ubicacion}
                            eventLat={selectedEvent?.latStart || selectedEvent?.lat}
                            eventLng={selectedEvent?.lngStart || selectedEvent?.lng}
                            eventNotes={selectedEvent?.notes || selectedEvent?.notas}
                            onSelectResult={null}
                            shortlistedIds={getShortlistedIds(selectedEvent?.id)}
                            onAddToShortlist={handleAddToShortlist}
                          />
                        </VStack>
                      </TabPanel>
                    )}
                  </TabPanels>
                </Tabs.Root>
              </Dialog.Body>
              <Dialog.Footer>
                {/* Solo mostrar botón si hay banda/cofradía asignada */}
                {selectedEvent && selectedEvent[otherPartyKey] && (
                  <>
                    <Button
                      colorPalette={userType === 'banda' && selectedEvent.status === 'pending' ? 'green' : 'blue'}
                      variant={userType === 'banda' && selectedEvent.status === 'pending' ? 'solid' : 'outline'}
                      mr={3}
                      onClick={() => {
                        if (userType === 'banda' && selectedEvent.status === 'pending') {
                          onClose();
                          handleShowInterest(selectedEvent);
                        } else {
                          onClose();
                          navigate(`${routePrefix}/mensajes`);
                        }
                      }}><Icon as={userType === 'banda' && selectedEvent.status === 'pending' ? FaHeart : FaEnvelope} />{userType === 'banda' && selectedEvent.status === 'pending' ? 'Me interesa' : 'Enviar mensaje'}</Button>
                    
                    {/* Para bandas en estado interesado: botón de confirmar acuerdo deshabilitado */}
                    {userType === 'banda' && selectedEvent.status === 'interested' && (
                      <Tooltip
                        content="La cofradía debe primero proponer un acuerdo. Podrás aceptarlo o rechazarlo desde el chat."
                        showArrow
                        positioning={{
                          placement: "top"
                        }}><span>
                          <Button colorPalette="green" mr={3} disabled><Icon as={FaCheckCircle} />Confirmar acuerdo
                                                </Button>
                        </span></Tooltip>
                    )}
                  </>
                )}
                <Button variant="ghost" onClick={onClose}>
                  Cerrar
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      {/* Modal de crear evento (solo para cofradías) */}
      {userType === 'cofradia' && (
        <Dialog.Root open={isCreateOpen} size='xl' onOpenChange={e => {
          if (!e.open) {
            closeCreateModal();
          }
        }}>
          <Portal>

            <Dialog.Backdrop />
            <Dialog.Positioner>
              <Dialog.Content>
                <Dialog.Header w="full">
                  <VStack align="stretch" gap={3} w="full">
                    <HStack justify="space-between" align="center" w="full">
                      <HStack>
                        <Icon as={FaPlus} color="purple.600" />
                        <Text>Crear nuevo evento</Text>
                      </HStack>
                      <Badge colorPalette="purple" variant="subtle" rounded="full" px={3} py={1}>
                        Paso {createStep + 1} / {CREATE_STEPS.length}
                      </Badge>
                    </HStack>
                    <Box w="full">
                      <Text fontSize="sm" fontWeight="700">
                        {CREATE_STEPS[createStep]?.title}
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {CREATE_STEPS[createStep]?.subtitle}
                      </Text>
                    </Box>
                    <Progress.Root
                      value={(createStep / (CREATE_STEPS.length - 1)) * 100}
                      size="sm"
                      colorPalette="purple"
                      rounded="full"
                      w="full"
                    >
                      <Progress.Track w="full" bg="gray.200">
                        <Progress.Range />
                      </Progress.Track>
                    </Progress.Root>
                  </VStack>
                </Dialog.Header>
                <Dialog.CloseTrigger />
                <Dialog.Body pb={6}>
                  <VStack gap={5} align="stretch">
                    {createStep === 0 && (
                      <VStack gap={4} align="stretch">
                        <Field.Root required>
                          <Field.Label>Nombre del evento</Field.Label>
                          <Input
                            placeholder="Ej: Procesión del Domingo de Ramos"
                            value={newEvent.eventName}
                            onChange={(e) => setNewEvent({ ...newEvent, eventName: e.target.value })}
                          />
                        </Field.Root>

                        <Field.Root>
                          <Field.Label>Descripción</Field.Label>
                          <Textarea
                            placeholder="Añade una breve descripción (opcional): recorrido, contexto, requisitos..."
                            value={newEvent.description}
                            onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                            rows={4}
                          />
                          <Field.HelperText>Esta descripción ayuda a las bandas a entender mejor el evento.</Field.HelperText>
                        </Field.Root>
                      </VStack>
                    )}

                    {createStep === 1 && (
                      <VStack gap={4} align="stretch">
                        <SimpleGrid columns={{ base: 1, md: 2 }} gap={4} w="full">
                          <Field.Root required>
                            <Field.Label>Fecha</Field.Label>
                            <Input
                              type="date"
                              value={newEvent.date}
                              min={new Date().toISOString().split('T')[0]}
                              onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                            />
                          </Field.Root>

                          <Field.Root required>
                            <Field.Label>Hora inicio</Field.Label>
                            <Input
                              type="time"
                              value={newEvent.timeStart}
                              min={userType === 'cofradia' && isHoySeleccionado ? minHoraHoy : undefined}
                              onChange={(e) => setNewEvent({ ...newEvent, timeStart: e.target.value })}
                            />
                          </Field.Root>
                        </SimpleGrid>

                        <Field.Root required>
                          <Field.Label>Hora final</Field.Label>
                          <Input
                            type="time"
                            value={newEvent.timeEnd}
                            onChange={(e) => setNewEvent({ ...newEvent, timeEnd: e.target.value })}
                          />
                          <Field.HelperText>
                            Obligatoria. Si la hora final es anterior a la de inicio, se asume que termina al día siguiente
                            (p. ej. 22:00–00:00 o 22:00–04:00).
                          </Field.HelperText>
                        </Field.Root>

                        <Field.Root required>
                          <Field.Label>Duración</Field.Label>
                          <Input
                            placeholder="Ej: 4 horas"
                            value={newEvent.duration}
                            onChange={(e) => setNewEvent({ ...newEvent, duration: e.target.value })}
                            readOnly
                          />
                          <Field.HelperText>Calculada automáticamente a partir de Hora inicio y Hora final.</Field.HelperText>
                        </Field.Root>
                      </VStack>
                    )}

                    {createStep === 2 && (
                      <VStack gap={4} align="stretch" w="full">
                        <Field.Root required w="full" alignItems="stretch">
                          <Field.Label>Ubicación de salida</Field.Label>
                          <Box position="relative" w="full">
                            <Input
                              w="full"
                              placeholder="Ej: Iglesia de San Pedro, Sevilla"
                              value={newEvent.locationStart}
                              onChange={(e) => setNewEvent({ ...newEvent, locationStart: e.target.value })}
                              onFocus={() => {
                                if (startSuggestions.length > 0) setShowStartSuggestions(true);
                              }}
                              autoComplete="off"
                            />
                            {showStartSuggestions && startSuggestions.length > 0 && (
                              <Box
                                position="absolute"
                                top="100%"
                                left={0}
                                right={0}
                                w="full"
                                bg="white"
                                border="1px"
                                borderColor="gray.200"
                                borderRadius="md"
                                mt={1}
                                maxH="200px"
                                overflowY="auto"
                                zIndex={1000}
                                boxShadow="lg"
                              >
                                {startSuggestions.map((suggestion, index) => (
                                  <Box
                                    key={index}
                                    p={3}
                                    cursor="pointer"
                                    _hover={{ bg: 'purple.50' }}
                                    borderBottom={index < startSuggestions.length - 1 ? '1px' : 'none'}
                                    borderColor="gray.100"
                                    onClick={() => {
                                      setNewEvent((p) => ({
                                        ...p,
                                        locationStart: suggestion.displayName,
                                        locationEnd: p.sameLocation ? suggestion.displayName : p.locationEnd,
                                      }));
                                      setStartCoordinates({ lat: suggestion.lat, lng: suggestion.lng });
                                      if (newEvent.sameLocation) setEndCoordinates({ lat: suggestion.lat, lng: suggestion.lng });
                                      setShowStartSuggestions(false);
                                      setStartSuggestions([]);
                                    }}
                                  >
                                    <HStack gap={2}>
                                      <Icon as={FaMapMarkerAlt} color="purple.500" flexShrink={0} />
                                      <Text fontSize="sm">{suggestion.displayName}</Text>
                                    </HStack>
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </Box>
                          {isGeocodingStart ? (
                            <Field.HelperText color="blue.500">
                              <HStack gap={2}>
                                <Spinner size="xs" />
                                <Text>Buscando ubicaciones...</Text>
                              </HStack>
                            </Field.HelperText>
                          ) : null}
                          {!isGeocodingStart && startCoordinates && !showStartSuggestions ? (
                            <Field.HelperText color="green.500">✓ Ubicación seleccionada</Field.HelperText>
                          ) : null}
                          {!isGeocodingStart && startGeocodingError && !showStartSuggestions ? (
                            <Field.HelperText color="orange.500">⚠ {startGeocodingError} (se guardará como texto)</Field.HelperText>
                          ) : null}
                        </Field.Root>

                        <HStack w="full" justify="space-between">
                          <Text fontSize="sm" fontWeight="600">
                            ¿La llegada coincide con la salida?
                          </Text>
                          <Switch
                            checked={newEvent.sameLocation}
                            onChange={(e) => {
                              const same = e.target.checked;
                              setNewEvent((p) => ({
                                ...p,
                                sameLocation: same,
                                locationEnd: same ? p.locationStart : p.locationEnd,
                              }));
                              if (same) setEndCoordinates(startCoordinates);
                            }}
                          />
                        </HStack>

                        {!newEvent.sameLocation && (
                          <Field.Root required w="full" alignItems="stretch">
                            <Field.Label>Ubicación de entrada</Field.Label>
                            <Box position="relative" w="full">
                              <Input
                                w="full"
                                placeholder="Ej: Catedral de Sevilla, Sevilla"
                                value={newEvent.locationEnd}
                                onChange={(e) => setNewEvent({ ...newEvent, locationEnd: e.target.value })}
                                onFocus={() => {
                                  if (endSuggestions.length > 0) setShowEndSuggestions(true);
                                }}
                                autoComplete="off"
                              />
                              {showEndSuggestions && endSuggestions.length > 0 && (
                                <Box
                                  position="absolute"
                                  top="100%"
                                  left={0}
                                  right={0}
                                  w="full"
                                  bg="white"
                                  border="1px"
                                  borderColor="gray.200"
                                  borderRadius="md"
                                  mt={1}
                                  maxH="200px"
                                  overflowY="auto"
                                  zIndex={1000}
                                  boxShadow="lg"
                                >
                                  {endSuggestions.map((suggestion, index) => (
                                    <Box
                                      key={index}
                                      p={3}
                                      cursor="pointer"
                                      _hover={{ bg: 'purple.50' }}
                                      borderBottom={index < endSuggestions.length - 1 ? '1px' : 'none'}
                                      borderColor="gray.100"
                                      onClick={() => {
                                        setNewEvent({ ...newEvent, locationEnd: suggestion.displayName });
                                        setEndCoordinates({ lat: suggestion.lat, lng: suggestion.lng });
                                        setShowEndSuggestions(false);
                                        setEndSuggestions([]);
                                      }}
                                    >
                                      <HStack gap={2}>
                                        <Icon as={FaMapMarkerAlt} color="purple.500" flexShrink={0} />
                                        <Text fontSize="sm">{suggestion.displayName}</Text>
                                      </HStack>
                                    </Box>
                                  ))}
                                </Box>
                              )}
                            </Box>
                            {isGeocodingEnd ? (
                              <Field.HelperText color="blue.500">
                                <HStack gap={2}>
                                  <Spinner size="xs" />
                                  <Text>Buscando ubicaciones...</Text>
                                </HStack>
                              </Field.HelperText>
                            ) : null}
                            {!isGeocodingEnd && endCoordinates && !showEndSuggestions ? (
                              <Field.HelperText color="green.500">✓ Ubicación seleccionada</Field.HelperText>
                            ) : null}
                            {!isGeocodingEnd && endGeocodingError && !showEndSuggestions ? (
                              <Field.HelperText color="orange.500">⚠ {endGeocodingError} (se guardará como texto)</Field.HelperText>
                            ) : null}
                          </Field.Root>
                        )}

                        {(startCoordinates || endCoordinates) && (
                          <Box w="full" borderWidth="1px" borderColor="gray.200" rounded="lg" overflow="hidden">
                            <ActuacionMapPreview
                              lat={startCoordinates?.lat}
                              lng={startCoordinates?.lng}
                              latFin={(newEvent.sameLocation ? startCoordinates : endCoordinates)?.lat}
                              lngFin={(newEvent.sameLocation ? startCoordinates : endCoordinates)?.lng}
                              height="240px"
                            />
                          </Box>
                        )}
                      </VStack>
                    )}

                    {createStep === 3 && (
                      <VStack gap={4} align="stretch">
                        <Field.Root>
                          <Field.Label>Notas adicionales</Field.Label>
                          <Textarea
                            placeholder="Información adicional sobre el evento..."
                            value={newEvent.notes}
                            onChange={(e) => setNewEvent({ ...newEvent, notes: e.target.value })}
                            rows={4}
                          />
                        </Field.Root>
                      </VStack>
                    )}

                    {createStep === 4 && (
                      <Box w="full">
                        <VStack align="stretch" gap={4}>
                          <Field.Root>
                            <Field.Label>Cartel / imagen principal</Field.Label>
                            <Input
                              type="file"
                              accept="image/*"
                              onChange={(e) => {
                                const f = e.target.files?.[0] || null;
                                setNewEventCartel(f);
                              }}
                            />
                            <Field.HelperText>Opcional. Imagen principal que aparecerá como cartel del evento.</Field.HelperText>
                            {newEventCartel && (
                              <HStack mt={3} gap={3} align="center">
                                <Image
                                  src={URL.createObjectURL(newEventCartel)}
                                  alt="Cartel"
                                  boxSize="72px"
                                  objectFit="cover"
                                  rounded="md"
                                  borderWidth="1px"
                                  borderColor="gray.200"
                                />
                                <VStack align="start" gap={0} flex={1}>
                                  <Text fontSize="sm" fontWeight="600">
                                    {newEventCartel.name}
                                  </Text>
                                  <Text fontSize="xs" color="gray.500">
                                    {(newEventCartel.size / 1024 / 1024).toFixed(2)} MB
                                  </Text>
                                </VStack>
                                <IconButton
                                  aria-label="Quitar cartel"
                                  size="sm"
                                  variant="ghost"
                                  colorPalette="red"
                                  onClick={() => setNewEventCartel(null)}><Icon as={FaTrash} /></IconButton>
                              </HStack>
                            )}
                          </Field.Root>

                          <Field.Root>
                            <Field.Label>Galería de imágenes</Field.Label>
                            <Input
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={(e) => {
                                const files = Array.from(e.target.files || []);
                                setNewEventFotos(files);
                              }}
                            />
                            <Field.HelperText>Opcional. Puedes seleccionar varias imágenes.</Field.HelperText>

                            {newEventFotos.length > 0 && (
                              <Wrap mt={3} gap={2}>
                                {newEventFotos.map((f) => (
                                  <WrapItem key={`${f.name}-${f.size}-${f.lastModified}`}>
                                    <Tag.Root size="sm" borderRadius="full" variant="subtle" colorPalette="purple" maxW="260px">
                                      <HStack gap={2}>
                                        <Icon as={FaImageIcon} />
                                        <Text fontSize="xs" lineClamp={1}>
                                          {f.name}
                                        </Text>
                                      </HStack>
                                    </Tag.Root>
                                  </WrapItem>
                                ))}
                              </Wrap>
                            )}
                          </Field.Root>

                          <Field.Root>
                            <Field.Label>Vídeos (YouTube)</Field.Label>
                            <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
                              <Input
                                placeholder="Título (opcional)"
                                value={newEventVideoDraft.titulo}
                                onChange={(e) => setNewEventVideoDraft((p) => ({ ...p, titulo: e.target.value }))}
                              />
                              <Input
                                placeholder="Enlace de YouTube"
                                value={newEventVideoDraft.url}
                                onChange={(e) => setNewEventVideoDraft((p) => ({ ...p, url: e.target.value }))}
                              />
                            </SimpleGrid>
                            <HStack mt={3}>
                              <Button
                                size="sm"
                                colorPalette="red"
                                variant="outline"
                                onClick={() => {
                                  const url = String(newEventVideoDraft.url || '').trim();
                                  if (!url) return;
                                  setNewEventVideos((prev) => [
                                    ...prev,
                                    { titulo: newEventVideoDraft.titulo?.trim() || undefined, url },
                                  ]);
                                  setNewEventVideoDraft({ titulo: '', url: '' });
                                }}
                                disabled={!String(newEventVideoDraft.url || '').trim()}><Icon as={FaYoutube} />Añadir vídeo
                                                          </Button>
                              <Field.HelperText m={0}>
                                Admite enlaces `watch?v=...`, `youtu.be/...` y `youtube.com/shorts/...`.
                              </Field.HelperText>
                            </HStack>

                            {newEventVideos.length > 0 && (
                              <VStack align="stretch" mt={4} gap={2}>
                                {newEventVideos.map((v, idx) => (
                                  <HStack
                                    key={`${v.url}-${idx}`}
                                    p={2}
                                    borderWidth="1px"
                                    borderColor="gray.200"
                                    rounded="md"
                                    justify="space-between"
                                  >
                                    <HStack gap={2} minW={0}>
                                      <Icon as={FaLink} color="gray.500" />
                                      <VStack gap={0} align="start" minW={0}>
                                        <Text fontSize="sm" fontWeight="600" lineClamp={1}>
                                          {v.titulo || 'Vídeo'}
                                        </Text>
                                        <Text fontSize="xs" color="gray.500" lineClamp={1}>
                                          {v.url}
                                        </Text>
                                      </VStack>
                                    </HStack>
                                    <IconButton
                                      aria-label="Quitar vídeo"
                                      size="sm"
                                      variant="ghost"
                                      colorPalette="red"
                                      onClick={() => setNewEventVideos((prev) => prev.filter((_, i) => i !== idx))}><Icon as={FaTrash} /></IconButton>
                                  </HStack>
                                ))}
                              </VStack>
                            )}
                          </Field.Root>
                        </VStack>
                      </Box>
                    )}
                  </VStack>
                </Dialog.Body>
                <Dialog.Footer>
                  <HStack w="full" justify="space-between">
                    <Button
                      variant="ghost"
                      onClick={() => setCreateStep((s) => Math.max(0, s - 1))}
                      disabled={createStep === 0}
                    >
                      Atrás
                    </Button>

                    <HStack>
                      <Button variant="ghost" onClick={closeCreateModal}>
                        Cancelar
                      </Button>

                      {createStep < CREATE_STEPS.length - 1 ? (
                        <Button
                          colorPalette="purple"
                          onClick={() => setCreateStep((s) => Math.min(CREATE_STEPS.length - 1, s + 1))}
                          disabled={!canAdvanceCreateStep(createStep)}
                        >
                          Siguiente
                        </Button>
                      ) : (
                        <Button
                          colorPalette="purple"
                          onClick={handleCreateEvent}
                          disabled={!canAdvanceCreateStep(0) || !canAdvanceCreateStep(1) || !canAdvanceCreateStep(2)}
                        >
                          Crear evento
                        </Button>
                      )}
                    </HStack>
                  </HStack>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>

          </Portal>
        </Dialog.Root>
      )}

      {/* Modal proponer acuerdo (asistente por pasos) */}
      <Dialog.Root open={isConfirmAcuerdoOpen} size='xl' placement='center' onOpenChange={e => {
        if (!e.open) {
          onConfirmAcuerdoClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop bg="blackAlpha.400" backdropFilter="blur(10px)" />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header bg="purple.500" color="white" roundedTop="md" pb={4}>
                <VStack align="stretch" gap={3}>
                  <HStack justify="space-between" align="flex-start" gap={3}>
                    <HStack gap={3}>
                      <Icon as={FaHandshake} w={6} h={6} />
                      <Text fontWeight="700" fontSize="lg">
                        Proponer acuerdo
                      </Text>
                    </HStack>
                    <Badge
                      colorPalette="purple"
                      variant="solid"
                      rounded="full"
                      px={3}
                      py={1}
                      bg="whiteAlpha.900"
                      color="purple.700"
                    >
                      Paso {acuerdoProposalStep + 1} / {EVENT_ACUERDO_PROPOSE_STEPS.length}
                    </Badge>
                  </HStack>
                  <Box>
                    <Text fontSize="sm" fontWeight="700">
                      {EVENT_ACUERDO_PROPOSE_STEPS[acuerdoProposalStep]?.title}
                    </Text>
                    <Text fontSize="xs" color="purple.100">
                      {EVENT_ACUERDO_PROPOSE_STEPS[acuerdoProposalStep]?.subtitle}
                    </Text>
                  </Box>
                  <Progress.Root
                    value={(acuerdoProposalStep / (EVENT_ACUERDO_PROPOSE_STEPS.length - 1)) * 100}
                    size="sm"
                    colorPalette="whiteAlpha"
                    bg="whiteAlpha.300"
                    rounded="full"
                    w="full"
                  >
                    <Progress.Track w="full" bg="whiteAlpha.300">
                      <Progress.Range />
                    </Progress.Track>
                  </Progress.Root>
                </VStack>
              </Dialog.Header>
              <Dialog.CloseTrigger color="white" />
              <Dialog.Body py={6}>
                {selectedBandaForAcuerdo && (
                  <VStack gap={6} align="stretch">
                    {acuerdoProposalStep === 0 && (
                      <Box
                        bg="green.50"
                        p={5}
                        rounded="xl"
                        borderWidth="2px"
                        borderColor="green.200"
                      >
                        <VStack gap={4} align="stretch">
                          <HStack gap={3}>
                            <Avatar.Root size="lg"><Avatar.Fallback name={selectedBandaForAcuerdo.banda.name}>{<Icon as={FaMusic} />}</Avatar.Fallback><Avatar.Image src={selectedBandaForAcuerdo.banda.avatar} /></Avatar.Root>
                            <VStack align="start" gap={1} flex={1}>
                              <Text fontSize="xl" fontWeight="bold" color="green.800">
                                {selectedBandaForAcuerdo.banda.name}
                              </Text>
                              <HStack gap={1}>
                                <Icon as={FaMapMarkerAlt} color="green.600" boxSize={4} />
                                <Text fontSize="sm" color="green.700">
                                  {selectedBandaForAcuerdo.banda.location}
                                </Text>
                              </HStack>
                            </VStack>
                          </HStack>

                          <Separator borderColor="green.300" />

                          <VStack gap={2} align="stretch">
                            <HStack>
                              <Icon as={FaCalendarCheck} color="green.600" boxSize={4} />
                              <Text fontSize="sm" fontWeight="medium" color="green.900">
                                Evento:
                              </Text>
                              <Text fontSize="sm" color="green.700">
                                {selectedBandaForAcuerdo.evento.eventName}
                              </Text>
                            </HStack>
                            <HStack>
                              <Icon as={FaClock} color="green.600" boxSize={4} />
                              <Text fontSize="sm" fontWeight="medium" color="green.900">
                                Fecha:
                              </Text>
                              <Text fontSize="sm" color="green.700">
                                {new Date(selectedBandaForAcuerdo.evento.date).toLocaleDateString('es-ES', {
                                  weekday: 'long',
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric',
                                })}
                              </Text>
                            </HStack>
                            <HStack>
                              <Icon as={FaMapMarkerAlt} color="green.600" boxSize={4} />
                              <Text fontSize="sm" fontWeight="medium" color="green.900">
                                Ubicación:
                              </Text>
                              <Text fontSize="sm" color="green.700">
                                {selectedBandaForAcuerdo.evento.location}
                              </Text>
                            </HStack>
                          </VStack>
                        </VStack>
                      </Box>
                    )}

                    {acuerdoProposalStep === 1 && (
                      <Field.Root required>
                        <Field.Label fontSize="sm">Descripción del acuerdo</Field.Label>
                        <Textarea
                          value={acuerdoData.descripcion}
                          onChange={(e) =>
                            setAcuerdoData({ ...acuerdoData, descripcion: e.target.value })
                          }
                          placeholder="Describe los términos del acuerdo..."
                          rows={6}
                        />
                        <Field.HelperText fontSize="xs">
                          La banda verá este texto antes de aceptar o rechazar la propuesta.
                        </Field.HelperText>
                      </Field.Root>
                    )}

                    {acuerdoProposalStep === 2 && (
                      <VStack gap={4} align="stretch">
                        <Field.Root>
                          <Field.Label fontSize="sm">Precio acordado (€)</Field.Label>
                          <NumberInput.Root
                            value={String(acuerdoData.precio)}
                            onValueChange={(details) =>
                              setAcuerdoData({
                                ...acuerdoData,
                                precio: parseFloat(details.value) || 0,
                              })
                            }
                            min={0}
                            precision={2}
                          >
                            <NumberInput.Input placeholder="0.00" />
                            <NumberInput.Control>
                              <NumberInput.IncrementTrigger />
                              <NumberInput.DecrementTrigger />
                            </NumberInput.Control>
                          </NumberInput.Root>
                          <Field.HelperText fontSize="xs">
                            Indica el precio que ofreces a la banda por este evento (puede ser 0).
                          </Field.HelperText>
                        </Field.Root>

                        <Field.Root>
                          <Field.Label fontSize="sm">Notas adicionales</Field.Label>
                          <Textarea
                            value={acuerdoData.notas}
                            onChange={(e) =>
                              setAcuerdoData({ ...acuerdoData, notas: e.target.value })
                            }
                            placeholder="Añade cualquier información adicional relevante..."
                            rows={4}
                          />
                          <Field.HelperText fontSize="xs">
                            Horarios específicos, requisitos técnicos, condiciones especiales, etc.
                          </Field.HelperText>
                        </Field.Root>
                      </VStack>
                    )}

                    {acuerdoProposalStep === 3 && (
                      <VStack gap={4} align="stretch">
                        <Box bg="green.50" p={4} rounded="lg" borderWidth="1px" borderColor="green.200">
                          <Text fontSize="xs" fontWeight="600" color="green.800" textTransform="uppercase">
                            Banda
                          </Text>
                          <Text fontWeight="bold" color="green.900">
                            {selectedBandaForAcuerdo.banda.name}
                          </Text>
                          <Text fontSize="sm" color="green.700" mt={1}>
                            {selectedBandaForAcuerdo.evento.eventName} ·{' '}
                            {new Date(selectedBandaForAcuerdo.evento.date).toLocaleDateString('es-ES')}
                          </Text>
                        </Box>

                        <Box>
                          <Text fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase">
                            Descripción
                          </Text>
                          <Box
                            p={3}
                            bg="gray.50"
                            rounded="md"
                            maxH="140px"
                            overflowY="auto"
                            borderWidth="1px"
                            borderColor="gray.200"
                          >
                            <Text fontSize="sm" whiteSpace="pre-wrap">
                              {acuerdoData.descripcion}
                            </Text>
                          </Box>
                        </Box>

                        {(acuerdoData.precio > 0 || acuerdoData.notas?.trim()) && (
                          <VStack align="stretch" gap={2}>
                            {acuerdoData.precio > 0 && (
                              <HStack gap={2}>
                                <Icon as={FaEuroSign} color="green.600" />
                                <Text fontSize="sm" fontWeight="600">
                                  Precio: {acuerdoData.precio}€
                                </Text>
                              </HStack>
                            )}
                            {acuerdoData.notas?.trim() && (
                              <Box>
                                <Text fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase">
                                  Notas
                                </Text>
                                <Text fontSize="sm" whiteSpace="pre-wrap">
                                  {acuerdoData.notas}
                                </Text>
                              </Box>
                            )}
                          </VStack>
                        )}

                        <Box bg={useColorModeValue('orange.50', 'orange.900')} p={4} rounded="lg" borderWidth="1px" borderColor={useColorModeValue('orange.200', 'orange.700')}>
                          <Alert.Root
                            status="warning"
                            variant='subtle'
                            borderRadius="md"
                            borderStartWidth='3px'
                            borderStartColor='colorPalette.solid'>
                            <Alert.Indicator />
                            <Box>
                              <Alert.Title fontSize="sm">Aviso importante</Alert.Title>
                              <Alert.Description fontSize="xs" display="block">
                                Cadencya no se responsabiliza del contenido ni de los acuerdos pactados entre las partes y no
                                intermedia ningún trato legal. Cadencya únicamente facilita la conexión entre entidad y banda
                                mediante su servicio.
                              </Alert.Description>
                            </Box>
                          </Alert.Root>
                          <Field.Root mt={3} required>
                            <Checkbox.Root
                              onCheckedChange={(details) => setAcuerdoAceptaDisclaimer(details.checked === true)}
                              checked={acuerdoAceptaDisclaimer}
                            ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>He leído y acepto el aviso legal anterior
                                                        </Checkbox.Label></Checkbox.Root>
                          </Field.Root>
                        </Box>

                        <Alert.Root status="info" rounded="lg">
                          <Alert.Indicator />
                          <Box>
                            <Alert.Title fontSize="sm">¿Qué sucederá después?</Alert.Title>
                            <Alert.Description fontSize="xs">
                              Se enviará esta propuesta a la banda. Podrá aceptarla o rechazarla desde el chat. El
                              evento permanecerá en estado «Pendiente» hasta que la banda acepte.
                            </Alert.Description>
                          </Box>
                        </Alert.Root>
                      </VStack>
                    )}
                  </VStack>
                )}
              </Dialog.Body>
              <Dialog.Footer bg="gray.50" roundedBottom="md">
                <HStack w="full" justify="space-between" flexWrap="wrap" gap={2}>
                  <Button
                    variant="ghost"
                    onClick={() => setAcuerdoProposalStep((s) => Math.max(0, s - 1))}
                    disabled={acuerdoProposalStep === 0 || isSubmittingAcuerdoProposal}
                  >
                    Atrás
                  </Button>
                  <HStack gap={2}>
                    <Button variant="ghost" onClick={onConfirmAcuerdoClose} disabled={isSubmittingAcuerdoProposal}>
                      Cancelar
                    </Button>
                    {acuerdoProposalStep < EVENT_ACUERDO_PROPOSE_STEPS.length - 1 ? (
                      <Button
                        colorPalette="purple"
                        onClick={() =>
                          setAcuerdoProposalStep((s) =>
                            Math.min(EVENT_ACUERDO_PROPOSE_STEPS.length - 1, s + 1),
                          )
                        }
                        disabled={!canAdvanceAcuerdoProposalStep(acuerdoProposalStep)}
                      >
                        Siguiente
                      </Button>
                    ) : (
                      <Button
                        colorPalette="purple"
                        onClick={handleConfirmAcuerdo}
                        size="lg"
                        fontWeight="700"
                        px={6}
                        loading={isSubmittingAcuerdoProposal}
                        disabled={!acuerdoData.descripcion.trim() || !acuerdoAceptaDisclaimer}><Icon as={FaHandshake} />Enviar propuesta
                                          </Button>
                    )}
                  </HStack>
                </HStack>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      {/* Modal de confirmación de interés (para bandas) */}
      <Dialog.Root open={isInterestModalOpen} placement='center' onOpenChange={e => {
        if (!e.open) {
          onInterestModalClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop bg="blackAlpha.600" backdropFilter="blur(10px)" />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                <HStack gap={3}>
                  <Icon as={FaHeart} color="green.500" w={6} h={6} />
                  <Text>Mostrar Interés</Text>
                </HStack>
              </Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body pb={6}>
                {eventToShowInterest && (
                  <VStack gap={4} align="stretch">
                    <Text fontSize="md" lineHeight="tall">
                      Estás a punto de mostrar tu interés en este evento:
                    </Text>
                    
                    {/* Detalles del evento */}
                    <Box
                      bg="green.50"
                      p={4}
                      rounded="lg"
                      borderWidth="1px"
                      borderColor="green.200"
                    >
                      <VStack align="stretch" gap={2}>
                        <Text fontSize="lg" fontWeight="bold" color="green.800">
                          {eventToShowInterest.eventName}
                        </Text>
                        <HStack gap={4} flexWrap="wrap">
                          <HStack gap={2}>
                            <Icon as={FaCalendarCheck} color="green.600" />
                            <Text fontSize="sm" color="green.700">
                              {new Date(eventToShowInterest.date).toLocaleDateString('es-ES', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </Text>
                          </HStack>
                          <HStack gap={2}>
                            <Icon as={FaClock} color="green.600" />
                            <Text fontSize="sm" color="green.700">
                              {eventToShowInterest.time}
                            </Text>
                          </HStack>
                        </HStack>
                        <HStack gap={2}>
                          <Icon as={FaMapMarkerAlt} color="green.600" />
                          <Text fontSize="sm" color="green.700">
                            {eventToShowInterest.location}
                          </Text>
                        </HStack>
                        <HStack gap={2}>
                          <Icon as={FaChurch} color="green.600" />
                          <Text fontSize="sm" color="green.700" fontWeight="medium">
                            {eventToShowInterest.cofradiaName}
                          </Text>
                        </HStack>
                      </VStack>
                    </Box>

                    {/* Campo para mensaje opcional */}
                    <Field.Root>
                      <Field.Label fontSize="sm" fontWeight="medium">
                        Mensaje para la cofradía (opcional)
                      </Field.Label>
                      <Textarea
                        placeholder="Escribe un mensaje presentándote o con cualquier pregunta sobre el evento..."
                        value={interestMessage}
                        onChange={(e) => setInterestMessage(e.target.value)}
                        rows={3}
                        resize="vertical"
                      />
                    </Field.Root>

                    <Alert.Root status="info" rounded="lg">
                      <Alert.Indicator />
                      <Box>
                        <Alert.Title fontSize="sm">¿Qué sucederá después?</Alert.Title>
                        <Alert.Description fontSize="xs">
                          Se creará una conversación con la cofradía donde podrás coordinar los detalles 
                          del evento. Tu interés será notificado y el estado cambiará a <strong>conectado</strong>.
                        </Alert.Description>
                      </Box>
                    </Alert.Root>
                  </VStack>
                )}
              </Dialog.Body>
              <Dialog.Footer>
                <Button
                  variant="ghost"
                  mr={3}
                  onClick={() => {
                    onInterestModalClose();
                    setInterestMessage('');
                  }}
                  disabled={isShowingInterest}
                >
                  Cancelar
                </Button>
                <Button
                  colorPalette="green"
                  onClick={confirmShowInterest}
                  size="lg"
                  fontWeight="700"
                  px={8}
                  loading={isShowingInterest}
                  loadingText="Enviando..."><Icon as={FaHeart} />Confirmar interés
                              </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      {/* Modal de Reseña */}
      <ReviewForm
        isOpen={isReviewModalOpen}
        onClose={onReviewModalClose}
        evento={eventoParaResenar}
        onReviewSubmitted={handleReviewSubmitted}
        userType={userType}
      />

      {/* Modal de confirmar eliminación de evento */}
      <Dialog.Root open={isDeleteEventoOpen} size='lg' placement='center' onOpenChange={e => {
        if (!e.open) {
          onDeleteEventoClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop bg="blackAlpha.600" backdropFilter="blur(10px)" />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header bg="red.500" color="white" roundedTop="md">
                <HStack gap={3}>
                  <Icon as={FaExclamationTriangle} w={6} h={6} />
                  <Text>Cancelar Evento</Text>
                </HStack>
              </Dialog.Header>
              <Dialog.CloseTrigger color="white" />
              <Dialog.Body py={6}>
                {eventoToDelete && (
                  <VStack gap={5} align="stretch">
                    <Alert.Root status="error" rounded="lg">
                      <Alert.Indicator />
                      <Box flex={1}>
                        <Alert.Title fontSize="md" mb={1}>¿Estás seguro de cancelar este evento?</Alert.Title>
                        <Alert.Description fontSize="sm">
                          Esta acción es permanente y notificará a todas las bandas con las que has iniciado conversaciones.
                        </Alert.Description>
                      </Box>
                    </Alert.Root>

                    <Box
                      bg="gray.50"
                      p={5}
                      rounded="xl"
                      borderWidth="2px"
                      borderColor="red.200"
                    >
                      <VStack gap={3} align="start">
                        <HStack>
                          <Icon as={FaCalendarCheck} color="purple.500" boxSize={5} />
                          <Text fontWeight="bold" fontSize="lg">{eventoToDelete.eventName}</Text>
                        </HStack>
                        <Text fontSize="sm" color="gray.600">
                          {new Date(eventoToDelete.date).toLocaleDateString('es-ES', {
                            weekday: 'long',
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })}
                        </Text>
                      </VStack>
                    </Box>

                    <Field.Root required>
                      <Field.Label fontWeight="600" fontSize="md">
                        Motivo de la cancelación
                      </Field.Label>
                      <Textarea
                        placeholder="Explica por qué se cancela el evento (será enviado a todas las bandas contactadas)..."
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        rows={4}
                        resize="vertical"
                      />
                      <Field.HelperText fontSize="xs" color="gray.500">
                        Este mensaje será enviado a todas las conversaciones activas del evento
                      </Field.HelperText>
                    </Field.Root>
                  </VStack>
                )}
              </Dialog.Body>
              <Dialog.Footer bg="gray.50" roundedBottom="md">
                <Button
                  variant="ghost"
                  mr={3}
                  onClick={() => {
                    onDeleteEventoClose();
                    setCancelReason('');
                    setEventoToDelete(null);
                  }}
                >
                  No, mantener evento
                </Button>
                <Button
                  colorPalette="red"
                  onClick={handleDeleteEvento}
                  loading={isDeletingEvento}
                  disabled={!cancelReason.trim()}><Icon as={FaTrash} />Sí, cancelar evento
                              </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>
    </AppLayout>
  );
};

export default Events;

